// Organisations-Bereich — reine Render-Funktionen (NT-ORG-UI-01).
//
// Bewusst ohne DOM-Zugriff (kein `document`): jede Funktion bekommt fertige Daten und liefert einen
// HTML-String. Dadurch sind die Regeln aus dem Prompt (Autofill-Schutz X2, ausgegraut mit Grund
// T4/K4, zwei Währungsspalten K1, Empfänger nicht vorausgefüllt K6, feste Matrix-Zeilen H6, volle
// Orgs mit Grund V5) im Frontend-Vitest ohne DOM prüfbar (Muster: crimeOps.categories.ts).
// Zustand, Datenladen, Listener und der Lager-Dialog leben in org.ts.

// ─── Typen (gespiegelt vom Backend, kein Shared-Import für reine Anzeige-Werte) ────────────────

export interface OrgMemberView { playerId: string; username: string; rank: number; rankName: string; joinedAt: string; aktivitaet: string }
export interface OrgInviteView { id: string; orgName: string; invitedByUsername: string; createdAt: string; expiresAt: string; tributeShare: number }
export interface OrgSentInviteView { id: string; invitedUsername: string; status: string; createdAt: string; expiresAt: string }
export interface OrgDissolutionView { requestedAt: string; effectiveAt: string; requestedByUsername: string; canVeto: boolean }
export interface OrgPayoutRequestView {
  id: string; requestedByUsername: string; amountCents: string; currency: string; reason: string;
  status: string; createdAt: string; expiresAt: string;
}
export interface OrgKasseView {
  kasseNoxCents: string; kasseBlackMoneyCents: string;
  tributeShare: number; pendingTributeShare: number | null; tributeShareEffectiveFrom: string | null;
  entnahmeRemainingNoxCents: string; entnahmeRemainingBlackMoneyCents: string;
}
export interface OrgLogEntryView { id: string; type: string; message: string; createdAt: string }
export interface OrgUnterhaltView {
  naechsteAbbuchungAt: string | null; faelligerBetragCents: string; rueckstandCents: string; sperrStufe: 0 | 1 | 2;
}
export interface OrgTalentNodeView { stufe: number; name: string; beschreibung: string; erreicht: boolean }
export interface OrgTalentZweigView { zweig: string; stufe: number; knoten: OrgTalentNodeView[] }
export interface OrgZentraleView {
  stufe: number; maxStufe: number; effectiveMemberLimit: number; isAusbauMoeglich: boolean;
  ausbauCompletesAt: string | null; naechsteStufe?: { kostenNoxCents: string; dauerStunden: number };
}
export interface OrgBaumView {
  zentrale: OrgZentraleView; unterhalt: OrgUnterhaltView;
  orgXp: number; orgStufe: number; punkteVergeben: number; punkteVerfuegbar: number;
  maxPunkte: number; xpFuerNaechstenPunkt: number | null;
  respecSperreBis: string | null; naechsteRespecKostenCents: string; respecWartezeitStunden: number;
  zweige: OrgTalentZweigView[];
}
export interface OrgLagerInventoryItem { itemType: string; itemKey: string; quantity: number }
export interface OrgLagerView {
  propertyId: string; slots: number; currentLoad: number; inventory: OrgLagerInventoryItem[];
  entnahmeMinRank: number; canWithdraw: boolean; entnahmeRemainingSlots: number | null;
}
export type OrgBeitrittsart = 'OFFEN' | 'BEWERBUNG' | 'GESCHLOSSEN';

export interface OrgOverviewView {
  id: string; name: string; rangNamen: string[];
  myRank: number; myRankName: string; myRights: string[]; isTopRank: boolean;
  memberCount: number; topRankCount: number; memberLimit: number; zentralenStufe: number;
  unterhalt: OrgUnterhaltView;
  pendingDissolution: OrgDissolutionView | null;
  createdAt: string;
  beitrittsart: OrgBeitrittsart; beschreibung: string | null;
}
export interface OrgGruendungInfo {
  kostenNoxCents: string; kostenBlackMoneyCents: string;
  startkapitalNoxCents: string; startkapitalBlackMoneyCents: string;
  minLevel: number; beitrittMinLevel: number;
}
export interface OrgMyApplication { id: string; orgId: string; orgName: string; createdAt: string; expiresAt: string }
export interface OrgPageData {
  org: OrgOverviewView | null;
  invitesReceived: OrgInviteView[];
  gruendung: OrgGruendungInfo;
  myApplications: OrgMyApplication[];
}
export interface OrgKasseTabView {
  myRank: number; isTopRank: boolean; topRankName: string; canEditSatz: boolean; tributeShareMaxPercent: number;
  kasse: OrgKasseView;
  myOpenPayoutRequests: OrgPayoutRequestView[];
  pendingPayoutRequests: OrgPayoutRequestView[];
  bonusEmpfaenger: { playerId: string; username: string }[];
  log: OrgLogEntryView[];
}
export interface OrgFestesRechtView { label: string; stufen: boolean[] }
export interface OrgApplicationView { id: string; username: string; level: number; message: string; createdAt: string; expiresAt: string }
export interface OrgHierarchieTabView {
  myRank: number; isTopRank: boolean; myRights: string[]; rangNamen: string[];
  members: OrgMemberView[];
  rechteMatrix: Record<string, boolean[]>;
  festeRechte: OrgFestesRechtView[];
  aufstiegsBedingungen: Record<string, { typ: string; wert: number }>;
  autoAufstiegMaxStufe: number;
  pendingInvitesSent: OrgSentInviteView[];
  beitrittsart: OrgBeitrittsart; beschreibung: string | null; beitrittMinLevel: number;
  applications: OrgApplicationView[];
}
export type OrgVerkaufKanal = 'HEHLER' | 'SCHWARZMARKT' | 'STRASSENHANDEL';
/** NT-ORG-07b (S1) — Verkauf aus dem Org-Lager, gespiegelt vom Backend. */
export interface OrgVerkaufView {
  darfVerkaufen: boolean; limit: number; restHeute: number;
  items: { itemKey: string; label: string; quantity: number; kanaele: OrgVerkaufKanal[] }[];
}
export interface OrgLagerTabView { myRank: number; lager: OrgLagerView; log: OrgLogEntryView[]; verkauf: OrgVerkaufView }
// NT-ORG-05/NT-ORG-08 — Tab GELÄNDE (gespiegelt vom Backend, org-gebaeude.service.ts).
export type OrgGebaeudeTyp = 'UMSCHLAGLAGER' | 'GEWAECHSHALLE' | 'CHEMIEWERK' | 'BRENNEREI' | 'SPEDITION' | 'WACHHAUS' | 'KONTOR';
export type OrgGebaeudeZweck = 'LAGER' | 'ANBAU' | 'LABOR' | 'FAHRZEUGE' | 'SICHERHEIT' | 'VERWALTUNG';
export interface OrgGebaeudeItemView { itemType: string; itemKey: string; quantity: number }
export interface OrgAnlageNaechsteStufeView { zielStufe: number; wirkung: string; kaufKostenCents: string; ketteEinsatzCents: string; minuten: number }
export interface OrgGebaeudeKarteView {
  id: string; typ: OrgGebaeudeTyp | null; zweck: OrgGebaeudeZweck | null; label: string;
  anlagenStufe: number; maxStufe: number; wirkung: string; naechsteStufe: OrgAnlageNaechsteStufeView | null;
  ausbauFertigAm: string | null; fortschrittPercent: number; ketteLaeuft: boolean;
  slots: number; belegteSlots: number; inventory: OrgGebaeudeItemView[]; suspicionLevel: number;
  raidStatus: { riskLabel: string; dailyChancePercent: number; warningActive: boolean; raidPossibleAt: string | null; immuneUntil: string | null } | null;
  aktivePflanzen: number; aktiveKochvorgaenge: number;
  abrissErstattungCents: string; abrissSperrgrund: string | null; hatWarenpuffer: boolean;
}
export interface OrgGelaendeParzelleView {
  id: string; houseNumber: number; flaecheQm: number; ausbauCompletesAt: string | null;
  nextFlaecheQm: number | null; nextCostCents: string | null; nextMinutes: number | null;
  gebaeude: OrgGebaeudeKarteView | null;
}
export interface OrgGebaeudeKatalogView {
  typ: OrgGebaeudeTyp; label: string; zweck: OrgGebaeudeZweck; beschreibung: string;
  minFlaecheQm: number; baupreisCents: string; stufen: { stufe: number; wirkung: string }[]; bereitsGebaut: boolean;
}
export interface OrgGelaendeTabView {
  myRank: number; isTopRank: boolean; myRights: string[];
  name: string; districtName: string; entfernungSchritte: number | null; gleicherBezirk: boolean | null;
  risiko: { hoechsterFl: number; username: string | null; schutzReduktionPercent: number; wachhausReduktionPercent: number };
  parzellen: OrgGelaendeParzelleView[];
  katalog: OrgGebaeudeKatalogView[];
  anlagenAusbauBis: string | null;
  ketteBlockiert: string | null;
  orgLagerInventory: OrgGebaeudeItemView[];
  log: OrgLogEntryView[];
  /** NT-ORG-09 — Abschnitt SPEDITION. */
  spedition: OrgSpeditionView;
}
export type OrgFahrzeugZustand = 'FREI' | 'UNTERWEGS' | 'BESCHLAGNAHMT';
export interface OrgFahrzeugView {
  id: string; vehicleKey: string; label: string; capacitySlots: number; zustand: OrgFahrzeugZustand;
  seizedUntil: string | null; kautionCents: string; verkaufserloesCents: string;
}
export interface OrgSpeditionView {
  hatSpedition: boolean; anlagenStufe: number; plaetze: number; belegt: number; darfVerwalten: boolean;
  kasseNoxCents: string; fahrzeuge: OrgFahrzeugView[];
  katalog: { vehicleKey: string; label: string; capacitySlots: number; preisCents: string }[];
}
export interface OrgAusbauTabView {
  myRank: number; isTopRank: boolean; myRights: string[]; baum: OrgBaumView;
}
export type OrgJoinState = 'BEITRETEN' | 'BEWERBEN' | 'BEWORBEN' | 'GESPERRT';
export interface OrgDirectoryEntry {
  id: string; name: string; memberCount: number; memberLimit: number; zentralenStufe: number;
  tributeSharePercent: number; createdAt: string; beitrittsart: OrgBeitrittsart; beschreibung: string | null;
  gebaeudeAnzahl: number; hoechsteAnlagenStufe: number;
  joinState: OrgJoinState; blockedReason: string | null;
}
export interface OrgDirectoryResult {
  entries: OrgDirectoryEntry[]; page: number; pages: number; total: number;
  viewer: { hasOrg: boolean; beitrittMinLevel: number; openApplications: number; maxOpenApplications: number };
}
export interface OrgLeaderboardEntry { position: number; name: string; orgStufe: number; memberCount: number; punkteVergeben: number }
export interface OrgLeaderboardResult { entries: OrgLeaderboardEntry[]; page: number; pages: number; total: number }

// ─── Unter-Tabs (Teil B) ─────────────────────────────────────────────────────────

export type OrgSubTab = 'UEBERSICHT' | 'KASSE' | 'HIERARCHIE' | 'LAGER' | 'GELAENDE' | 'AUSBAU' | 'AUFTRAEGE' | 'VERZEICHNIS';

/** Reihenfolge der Unter-Tabs für Mitglieder. Ohne Org gibt es nur das Verzeichnis (V1). */
export const ORG_MEMBER_TABS: readonly [OrgSubTab, string][] = [
  ['UEBERSICHT', 'ÜBERSICHT'],
  ['AUFTRAEGE', 'AUFTRÄGE'],
  ['KASSE', 'KASSE'],
  ['HIERARCHIE', 'HIERARCHIE'],
  ['LAGER', 'LAGER'],
  ['GELAENDE', 'GELÄNDE'],
  ['AUSBAU', 'AUSBAU'],
  ['VERZEICHNIS', 'ORGANISATIONEN'],
];

// ─── Org-Aufträge (NT-ORG-07a) — gespiegelt vom Backend ───────────────────────────

export type OrgAufgabeZustand = 'OFFEN' | 'UEBERNOMMEN' | 'ERLEDIGT';
export interface OrgAufgabeView {
  id: string; stufe: number; aufgabenTyp: string; zustand: OrgAufgabeZustand;
  gebundeneSlots: number; stunden: number; lohnCents: string;
  istMeine: boolean; auftragId: string | null; erledigtVonName: string | null; zeitabzugMinuten: number | null;
  sperrgrund: string | null;
  /** NT-ORG-09 N3 — nur TRANSPORT: welches Fahrzeug die Übernahme nutzt. */
  orgFahrzeug: { vehicleKey: string; label: string } | null;
  eigenesFahrzeugNoetig: boolean;
}
export interface OrgStufeView {
  stufe: number; rangLabel: string; aufgabenGesamt: number; erledigt: number; lohnJeAufgabeCents: string;
  aktuellerZeitabzugMinuten: number; maxZeitabzugMinuten: number; vorarbeitBisMax: number; istMeineStufe: boolean;
}
export interface OrgAuftragView {
  id: string; vorlageKey: string; name: string; status: 'LAUFEND' | 'ABGESCHLOSSEN' | 'ABGEBROCHEN';
  startedAt: string; grundEndsAt: string; minEndsAt: string; endsAt: string; completedAt: string | null;
  einsatzCents: string; einsatzRestCents: string; currency: string; kettenStufen: number;
  quote: number; orgXpVergeben: number | null;
  /** NT-ORG-08 — Anlagen-Kette: Zielgebäude und Zielstufe (sonst null). */
  anlageZiel: { label: string; zielStufe: number } | null;
  stufen: OrgStufeView[]; aufgaben: OrgAufgabeView[];
}
export interface OrgAuftragVorlageView {
  key: string; name: string; beschreibung: string; laufzeitTage: number; einsatzCents: string; currency: string;
  kettenStufen: number; aufgabenGesamt: number; endproduktLabel: string; endproduktMenge: number; orgXp: number;
}
export interface OrgAuftraegeTabView {
  istOberste: boolean; laufend: OrgAuftragView | null; letzter: OrgAuftragView | null; cooldownBis: string | null;
  vorlagen: OrgAuftragVorlageView[]; kasseNoxCents: string; kasseBlackMoneyCents: string;
  meineStufe: number | null; maxGleichzeitig: number;
  objekte: { id: string; label: string; freeSlots: number; type: string }[];
  paare: { quelleId: string; zielId: string; steps: number; maxSlots: number }[];
  fahrzeuge: { vehicleKey: string; label: string; capacitySlots: number }[];
}

// ─── Produktionsangebote (NT-ORG-07b) — gespiegelt vom Backend ─────────────────────

export type OrgAuftraegeAnsicht = 'KETTE' | 'PRODUKTION';
export type OrgFahndungsdruck = 'KEINER' | 'MITTEL' | 'HOCH';
export type OrgProdEbene = 'ENDPRODUKT' | 'VERARBEITUNG' | 'BESORGUNG';
export interface OrgBeteiligterView { name: string; laeufe: number }
export interface OrgBesorgungEbeneView {
  itemKey: string; label: string; bedarfJeVerarbeitung: number; mengeJeLauf: number; imLager: number;
  erledigt: number; laufend: number; lohnJeLaufCents: string; dauerMinuten: number; illegal: boolean;
  beteiligte: OrgBeteiligterView[]; sperrgrund: string | null;
}
export interface OrgVerarbeitungEbeneView {
  outputLabel: string; mengeJeLauf: number; erledigt: number; laufend: number; werkbaenke: number;
  lohnJeLaufCents: string; dauerMinuten: number; beteiligte: OrgBeteiligterView[]; sperrgrund: string | null;
}
export interface OrgProduktionView {
  id: string; angebotKey: string; name: string; status: 'LAUFEND' | 'ABGESCHLOSSEN' | 'ABGEBROCHEN';
  startedAt: string; endsAt: string; fensterEndeAt: string; completedAt: string | null;
  einsatzCents: string; einsatzRestCents: string; currency: string; endproduktLabel: string; produktMenge: number;
  besorgung: OrgBesorgungEbeneView[]; verarbeitung: OrgVerarbeitungEbeneView;
  verlauf: { playerName: string; typ: 'BESORGUNG' | 'VERARBEITUNG'; itemLabel: string; ausbeute: number | null; lohnCents: string; ereignisText: string | null; completedAt: string | null }[];
}
export interface OrgProduktionsAngebotView {
  key: string; name: string; beschreibung: string; endproduktLabel: string; currency: string;
  fahndungsdruck: OrgFahndungsdruck;
  zutaten: { itemKey: string; label: string; menge: number; illegal: boolean }[];
  laufzeiten: { tage: number; einsatzCents: string }[];
  besorgungMenge: number; besorgungMinuten: number; verarbeitungMinuten: number; outputJeLauf: number;
  lohnBesorgungCents: string; lohnVerarbeitungCents: string; maxMengeJeTag: number; werkbaenke: number;
  sperrgrund: string | null;
}
export interface OrgProduktionTabView {
  istOberste: boolean; hatProduktionsrecht: boolean;
  laufend: OrgProduktionView | null; letzte: OrgProduktionView | null; cooldownBis: string | null;
  kasseNoxCents: string; kasseBlackMoneyCents: string;
  angebote: OrgProduktionsAngebotView[];
  meinLauf: { id: string; typ: 'BESORGUNG' | 'VERARBEITUNG'; itemLabel: string; menge: number; startedAt: string; endsAt: string; wartetAufPlatz: boolean } | null;
  beschaffungsFensterProzent: number;
}

export function renderSubTabs(active: OrgSubTab): string {
  return `
    <nav class="org-subtabs" aria-label="Organisation">
      ${ORG_MEMBER_TABS.map(([key, label]) => `
        <button type="button" class="org-subtab-btn ${key === active ? 'is-active' : ''}" data-action="org-tab" data-org-tab="${key}">${label}</button>
      `).join('')}
    </nav>
  `;
}

// ─── Konstanten (reine Anzeige, gespiegelt vom Backend) ──────────────────────────

export const RANG_VORLAGEN = ['BETRIEB', 'FAMILIE', 'STRASSE', 'KUTTE'] as const;

// NT-ORG-01-NACHTRAG-01: Kasse-/Lager-Entnahme sind fest an die Rangstufe gebunden (wie die
// Auflösung), kein OrgRecht — sie erscheinen als feste Zeilen (H6), nicht hier.
export const ORG_RECHTE = [
  'EINLADEN', 'RAUSWERFEN', 'RANG_VERGEBEN',
  'SKILLEN', 'SATZ_AENDERN', 'RANGNAMEN_AENDERN', 'BEDINGUNGEN_SETZEN', 'BAUEN', 'PRODUKTION', 'VERKAUFEN',
] as const;

const AUFSTIEG_TYPEN: readonly [string, string][] = [
  ['KEINE', 'Kein automatischer Aufstieg'],
  ['MITGLIEDSDAUER', 'Mitgliedsdauer (Tage)'],
  ['EINZAHLUNGEN', 'Einzahlungen gesamt (₦/S-₦)'],
  ['AUFTRAEGE', 'Abgeschlossene Aufträge'],
];
const ZWEIG_LABELS: Record<string, string> = {
  LOGISTIK: 'LOGISTIK', SCHUTZ: 'SCHUTZ', GESCHAEFT: 'GESCHÄFT', EINFLUSS: 'EINFLUSS', NETZWERK: 'NETZWERK',
};
const BEITRITTSART_LABEL: Record<OrgBeitrittsart, string> = {
  OFFEN: 'OFFEN — sofortiger Beitritt',
  BEWERBUNG: 'BEWERBUNG — oberste Stufe entscheidet',
  GESCHLOSSEN: 'GESCHLOSSEN — nur auf Einladung',
};
/** Spiegel von ORG_BESCHREIBUNG_MAX_LENGTH / ORG_BEWERBUNG_TEXT_MAX_LENGTH (org.constants.ts). */
export const ORG_BESCHREIBUNG_MAX_LENGTH = 280;
export const ORG_BEWERBUNG_TEXT_MAX_LENGTH = 200;
const ORG_ANTRAG_BEGRUENDUNG_MAX_LENGTH = 200;

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────────

/** HTML-Escape ohne DOM (die Datei muss im Node-Vitest laufen). */
export function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Cent-String (BigInt-Serialisierung) als Dezimalwert — währungsneutral. */
export function fmtAmount(cents: string): string {
  return (Number(cents) / 100).toFixed(2).replace('.', ',');
}

/** Bruchteil (0.3) als Prozent-Text ("30", "12,5") — dieselbe Einheit wie das Eingabefeld (X1). */
export function fmtPercent(share: number): string {
  const percent = Math.round(share * 1000) / 10;
  return Number.isInteger(percent) ? String(percent) : percent.toFixed(1).replace('.', ',');
}

function fmtAlter(iso: string, now: Date): string {
  const minuten = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minuten < 60) return `vor ${String(minuten)} Min.`;
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return `vor ${String(stunden)} Std.`;
  return `vor ${String(Math.floor(stunden / 24))} Tagen`;
}

function currencySymbol(currency: string): string {
  return currency === 'NOX' ? '₦' : 'S-₦';
}

export function disabledAttr(blocked: boolean): string {
  return blocked ? 'disabled' : '';
}

/** T4 — ausgegraut mit sichtbarer Begründung, nie unsichtbar. */
export function renderGrayedHint(blocked: boolean, reason: string): string {
  return blocked ? `<p class="org-grayed-hint">${esc(reason)}</p>` : '';
}

/**
 * X2 — Pflicht-Attribute für JEDES Textfeld im Org-Bereich: kein Browser-Autofill. Feldnamen sind
 * bewusst neutral (nie "username"/"email"/"name"), sonst hängen Browser die Login-Daten an.
 */
export const NO_AUTOFILL = 'autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" data-lpignore="true" data-1p-ignore="true"';

function textInput(name: string, opts: { placeholder: string; maxlength?: number; value?: string; disabled?: boolean; inputmode?: string; required?: boolean }): string {
  const maxlength = opts.maxlength !== undefined ? ` maxlength="${String(opts.maxlength)}"` : '';
  const inputmode = opts.inputmode ? ` inputmode="${opts.inputmode}"` : '';
  return `<input type="text" name="${name}" value="${esc(opts.value ?? '')}" placeholder="${esc(opts.placeholder)}"${maxlength}${inputmode}`
    + ` ${NO_AUTOFILL} ${opts.required === false ? '' : 'required'} ${disabledAttr(opts.disabled === true)} />`;
}

function renderLogList(entries: OrgLogEntryView[], emptyText: string): string {
  if (entries.length === 0) return `<p class="org-hint">${esc(emptyText)}</p>`;
  return `<div class="org-log-list">${entries.map((l) => `
    <div class="org-log-entry">
      <span class="org-log-time">${fmtDate(l.createdAt)}</span>
      <span class="org-log-message">${esc(l.message)}</span>
    </div>
  `).join('')}</div>`;
}

// ─── ÜBERSICHT ────────────────────────────────────────────────────────────────────

function sperrStufeText(stufe: 0 | 1 | 2): string {
  if (stufe === 2) return 'Karenz abgelaufen — Ausbau und neue Mitglieder sind gesperrt, bis die Kasse reicht.';
  if (stufe === 1) return 'Karenzzeit läuft noch, danach greifen Sperren.';
  return '';
}

export function renderOverviewTab(org: OrgOverviewView, log: OrgLogEntryView[]): string {
  const isSolo = org.memberCount === 1;
  const u = org.unterhalt;
  const rueckstand = BigInt(u.rueckstandCents);
  const leaveBlocked = !isSolo && org.isTopRank && org.topRankCount <= 1;

  const dissolveBlocked = org.pendingDissolution !== null || !org.isTopRank;
  const dissolveReason = org.pendingDissolution !== null
    ? 'Es läuft bereits ein Auflösungsantrag.'
    : 'Nur die oberste Stufe darf die Auflösung beantragen.';

  return `
    ${org.pendingDissolution ? `
      <div class="org-banner org-banner--warn">
        <p>AUFLÖSUNG BEANTRAGT von ${esc(org.pendingDissolution.requestedByUsername)} — wirksam ab ${fmtDate(org.pendingDissolution.effectiveAt)}.</p>
        ${org.pendingDissolution.canVeto ? '<button class="btn-org btn-org--accent" data-action="veto-dissolution">STOPPEN</button>' : ''}
      </div>` : ''}
    ${rueckstand > 0n ? `
      <div class="org-banner org-banner--warn">
        <p>UNTERHALT IM RÜCKSTAND: ${fmtAmount(u.rueckstandCents)} ₦ — ${esc(sperrStufeText(u.sperrStufe))}</p>
      </div>` : ''}
    <div class="org-section org-section--header">
      <div>
        <p class="org-name">${esc(org.name)}</p>
        <p class="org-meta">Dein Rang: <strong>${esc(org.myRankName)}</strong> · seit ${fmtDate(org.createdAt)}</p>
      </div>
    </div>
    <div class="org-stat-grid">
      <div class="org-stat"><span class="org-stat-label">MITGLIEDER</span><span class="org-stat-value">${String(org.memberCount)} / ${String(org.memberLimit)}</span></div>
      <div class="org-stat"><span class="org-stat-label">ZENTRALE</span><span class="org-stat-value">STUFE ${String(org.zentralenStufe)}</span></div>
      <div class="org-stat"><span class="org-stat-label">UNTERHALT</span><span class="org-stat-value">${fmtAmount(u.faelligerBetragCents)} ₦ / WOCHE</span></div>
      <div class="org-stat"><span class="org-stat-label">NÄCHSTE ABBUCHUNG</span><span class="org-stat-value">${u.naechsteAbbuchungAt ? fmtDate(u.naechsteAbbuchungAt) : '—'}</span></div>
    </div>
    <div class="org-section">
      <p class="org-section-label">LETZTE EREIGNISSE</p>
      ${renderLogList(log, 'Noch keine Einträge.')}
    </div>
    <div class="org-section">
      <p class="org-section-label">MITGLIEDSCHAFT</p>
      ${isSolo ? '<p class="org-hint">Du bist das einzige Mitglied — Austreten löst die Organisation sofort auf (das Org-Lager muss leer sein).</p>' : ''}
      <div class="org-header-actions">
        <button class="btn-org btn-org--danger" data-action="leave-org" ${disabledAttr(leaveBlocked)}>AUSTRETEN</button>
        ${org.pendingDissolution === null ? `<button class="btn-org btn-org--danger" data-action="request-dissolution" ${disabledAttr(dissolveBlocked)}>ORGANISATION AUFLÖSEN</button>` : ''}
      </div>
      ${renderGrayedHint(leaveBlocked, 'Du bist das letzte Mitglied der obersten Stufe — ernenne vorher einen Nachfolger auf deine Stufe.')}
      ${org.pendingDissolution === null ? renderGrayedHint(dissolveBlocked, dissolveReason) : ''}
    </div>
  `;
}

// ─── KASSE (Teil C) ───────────────────────────────────────────────────────────────

function renderKasseColumn(tab: OrgKasseTabView, currency: 'NOX' | 'BLACK_MONEY'): string {
  const symbol = currencySymbol(currency);
  const balance = currency === 'NOX' ? tab.kasse.kasseNoxCents : tab.kasse.kasseBlackMoneyCents;
  const remaining = currency === 'NOX' ? tab.kasse.entnahmeRemainingNoxCents : tab.kasse.entnahmeRemainingBlackMoneyCents;
  const withdrawBlocked = !tab.isTopRank;

  return `
    <div class="org-kasse-col" data-currency="${currency}">
      <p class="org-kasse-col-label">${currency === 'NOX' ? 'NOX' : 'SCHWARZGELD'}</p>
      <p class="org-kasse-balance">${fmtAmount(balance)} ${symbol}</p>

      <p class="org-form-label">EINZAHLEN</p>
      <form class="org-form" data-form="kasse-deposit" data-currency="${currency}">
        ${textInput('betrag', { placeholder: `Betrag in ${symbol}`, inputmode: 'decimal' })}
        <button type="submit" class="btn-org btn-org--accent">EINZAHLEN</button>
      </form>
      <p class="org-hint">Eingezahltes Geld gehört der Org. Keine Rückerstattung bei Austritt.</p>

      <p class="org-form-label">ENTNEHMEN</p>
      <form class="org-form" data-form="kasse-withdraw" data-currency="${currency}">
        ${textInput('betrag', { placeholder: `Betrag in ${symbol}`, inputmode: 'decimal', disabled: withdrawBlocked })}
        <button type="submit" class="btn-org btn-org--accent" ${disabledAttr(withdrawBlocked)}>ENTNEHMEN</button>
      </form>
      ${withdrawBlocked
        ? renderGrayedHint(true, `Nur ${tab.topRankName} darf entnehmen — stell einen Antrag.`)
        : `<p class="org-hint">Restlimit heute: ${fmtAmount(remaining)} ${symbol}</p>`}

      ${tab.isTopRank ? '' : `
        <p class="org-form-label">ANTRAG STELLEN</p>
        <form class="org-form org-form--stacked" data-form="payout-request" data-currency="${currency}">
          ${textInput('betrag', { placeholder: `Betrag in ${symbol}`, inputmode: 'decimal' })}
          ${textInput('begruendung', { placeholder: 'Begründung', maxlength: ORG_ANTRAG_BEGRUENDUNG_MAX_LENGTH })}
          <button type="submit" class="btn-org btn-org--accent">ANTRAG STELLEN</button>
        </form>
      `}
    </div>
  `;
}

function renderPendingRequests(tab: OrgKasseTabView, now: Date): string {
  if (tab.isTopRank) {
    return `
      <div class="org-section">
        <p class="org-section-label">OFFENE ANTRÄGE</p>
        ${tab.pendingPayoutRequests.length === 0 ? '<p class="org-hint">Keine offenen Anträge.</p>' : `
          <div class="org-payout-list">
            ${tab.pendingPayoutRequests.map((r) => `
              <div class="org-payout-row">
                <div class="org-payout-info">
                  <span><strong>${esc(r.requestedByUsername)}</strong> · ${fmtAmount(r.amountCents)} ${currencySymbol(r.currency)} · ${fmtAlter(r.createdAt, now)}</span>
                  <span class="org-invite-meta">„${esc(r.reason)}"</span>
                </div>
                <div class="org-invite-actions">
                  <button class="btn-org btn-org--small btn-org--accent" data-action="approve-payout" data-request-id="${esc(r.id)}">GENEHMIGEN</button>
                  <button class="btn-org btn-org--small btn-org--danger" data-action="reject-payout" data-request-id="${esc(r.id)}">ABLEHNEN</button>
                </div>
                <form class="org-form" data-form="payout-approve-custom" data-request-id="${esc(r.id)}">
                  ${textInput('betrag', { placeholder: `Anderer Betrag in ${currencySymbol(r.currency)}`, inputmode: 'decimal' })}
                  <button type="submit" class="btn-org btn-org--small">ANDEREN BETRAG AUSZAHLEN</button>
                </form>
              </div>
            `).join('')}
          </div>
        `}
      </div>
    `;
  }
  return `
    <div class="org-section">
      <p class="org-section-label">MEINE ANTRÄGE</p>
      ${tab.myOpenPayoutRequests.length === 0 ? '<p class="org-hint">Keine eigenen offenen Anträge.</p>' : `
        <div class="org-payout-list">
          ${tab.myOpenPayoutRequests.map((r) => `
            <div class="org-payout-row">
              <span>${fmtAmount(r.amountCents)} ${currencySymbol(r.currency)} — „${esc(r.reason)}"</span>
              <span class="org-invite-meta">offen · läuft ab ${fmtDate(r.expiresAt)}</span>
            </div>
          `).join('')}
        </div>
      `}
    </div>
  `;
}

function renderBonusSection(tab: OrgKasseTabView): string {
  const blocked = !tab.isTopRank;
  return `
    <div class="org-section">
      <p class="org-section-label">BONUS AUSZAHLEN</p>
      <form class="org-form org-form--stacked" data-form="bonus">
        <select name="empfaenger" class="org-select" required ${disabledAttr(blocked)}>
          <option value="" disabled selected>— EMPFÄNGER WÄHLEN —</option>
          ${tab.bonusEmpfaenger.map((m) => `<option value="${esc(m.playerId)}">${esc(m.username)}</option>`).join('')}
        </select>
        ${textInput('betrag', { placeholder: 'Betrag', inputmode: 'decimal', disabled: blocked })}
        <div class="org-form">
          <button type="submit" class="btn-org btn-org--accent" data-bonus-currency="NOX" ${disabledAttr(blocked)}>IN ₦ AUSZAHLEN</button>
          <button type="submit" class="btn-org btn-org--accent" data-bonus-currency="BLACK_MONEY" ${disabledAttr(blocked)}>IN S-₦ AUSZAHLEN</button>
        </div>
      </form>
      ${renderGrayedHint(blocked, `Nur ${tab.topRankName} darf Boni auszahlen.`)}
    </div>
  `;
}

function renderSatzSection(tab: OrgKasseTabView): string {
  const k = tab.kasse;
  const blocked = !tab.canEditSatz;
  const pending = k.pendingTributeShare !== null && k.tributeShareEffectiveFrom !== null
    ? `<p class="org-meta">Künftig: <strong>${fmtPercent(k.pendingTributeShare)} %</strong> — WIRKT AB ${fmtDate(k.tributeShareEffectiveFrom)}</p>`
    : '';
  return `
    <div class="org-section">
      <p class="org-section-label">ABGABENSATZ</p>
      <p class="org-meta">Aktuell: <strong>${fmtPercent(k.tributeShare)} %</strong> deiner Brennpunkt-Abgaben gehen an die Org.</p>
      ${pending}
      <form class="org-form" data-form="tribute-share">
        <div class="org-percent-input">
          ${textInput('prozent', { placeholder: '0', value: fmtPercent(k.tributeShare), inputmode: 'decimal', maxlength: 5, disabled: blocked })}
          <span class="org-percent-unit">%</span>
        </div>
        <button type="submit" class="btn-org btn-org--accent" ${disabledAttr(blocked)}>ÄNDERN</button>
      </form>
      <p class="org-hint">Eingabe in Prozent, MAXIMAL ${fmtPercent(tab.tributeShareMaxPercent / 100)} %. Eine Erhöhung wirkt erst nach 48 Std., eine Senkung sofort.</p>
      ${renderGrayedHint(blocked, 'Dein Rang darf den Abgabensatz nicht ändern.')}
    </div>
  `;
}

export function renderKasseTab(tab: OrgKasseTabView, now: Date = new Date()): string {
  return `
    <div class="org-kasse-cols">
      ${renderKasseColumn(tab, 'NOX')}
      ${renderKasseColumn(tab, 'BLACK_MONEY')}
    </div>
    ${renderPendingRequests(tab, now)}
    ${renderBonusSection(tab)}
    ${renderSatzSection(tab)}
    <div class="org-section">
      <p class="org-section-label">KASSENBEWEGUNGEN</p>
      ${renderLogList(tab.log, 'Noch keine Kassenbewegungen.')}
    </div>
  `;
}

// ─── HIERARCHIE (Teil D) ─────────────────────────────────────────────────────────

function renderMemberLine(tab: OrgHierarchieTabView, m: OrgMemberView, myPlayerId: string): string {
  const canAssign = tab.myRights.includes('RANG_VERGEBEN') && m.rank <= tab.myRank;
  const canKick = tab.myRights.includes('RAUSWERFEN') && m.rank < tab.myRank && m.playerId !== myPlayerId;
  const rankControl = canAssign
    ? `<select class="org-rank-select" data-action="assign-rank" data-player-id="${esc(m.playerId)}" aria-label="Rang von ${esc(m.username)}">
        ${tab.rangNamen.map((name, idx) => {
          const rank = idx + 1;
          if (rank > tab.myRank) return '';
          return `<option value="${String(rank)}" ${rank === m.rank ? 'selected' : ''}>${esc(name)}</option>`;
        }).join('')}
      </select>`
    : '';
  return `
    <div class="org-member-row">
      <div class="org-member-info">
        <span class="org-member-name">${esc(m.username)}</span>
        <span class="org-invite-meta">dabei seit ${new Date(m.joinedAt).toLocaleDateString('de-DE')} · aktiv ${esc(m.aktivitaet)}</span>
      </div>
      <div class="org-invite-actions">
        ${rankControl}
        ${canKick ? `<button class="btn-org btn-org--small btn-org--danger" data-action="kick-member" data-player-id="${esc(m.playerId)}">RAUSWERFEN</button>` : ''}
      </div>
    </div>
  `;
}

function renderLadder(tab: OrgHierarchieTabView, myPlayerId: string): string {
  const stufen = [5, 4, 3, 2, 1];
  return stufen.map((stufe) => {
    const members = tab.members.filter((m) => m.rank === stufe);
    return `
      <div class="org-ladder-step">
        <p class="org-ladder-label"><span class="org-ladder-num">STUFE ${String(stufe)}</span> ${esc(tab.rangNamen[stufe - 1] ?? '')}</p>
        ${members.length === 0 ? '<p class="org-hint">—</p>' : members.map((m) => renderMemberLine(tab, m, myPlayerId)).join('')}
      </div>
    `;
  }).join('');
}

function renderMatrix(tab: OrgHierarchieTabView): string {
  const editable = tab.isTopRank;
  return `
    <div class="org-matrix-wrap">
      <table class="org-matrix-table">
        <thead><tr><th>RECHT</th>${tab.rangNamen.map((n) => `<th>${esc(n)}</th>`).join('')}</tr></thead>
        <tbody>
          ${ORG_RECHTE.map((recht) => `
            <tr>
              <td>${recht.replace(/_/g, ' ')}</td>
              ${(tab.rechteMatrix[recht] ?? [false, false, false, false, false]).map((val, idx) => `
                <td><input type="checkbox" data-matrix-recht="${recht}" data-matrix-stufe="${String(idx)}" ${val ? 'checked' : ''} ${disabledAttr(!editable)} /></td>
              `).join('')}
            </tr>
          `).join('')}
          ${tab.festeRechte.map((r) => `
            <tr class="org-matrix-fixed">
              <td>${esc(r.label)} <span class="org-matrix-fixed-tag">FEST</span></td>
              ${r.stufen.map((val) => `<td><input type="checkbox" ${val ? 'checked' : ''} disabled data-matrix-fixed="true" /></td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
    <p class="org-hint">FEST markierte Vorgänge hängen an der Rangstufe und sind nicht änderbar.</p>
    <button class="btn-org btn-org--accent" data-action="save-matrix" ${disabledAttr(!editable)}>MATRIX SPEICHERN</button>
    ${renderGrayedHint(!editable, 'Nur die oberste Stufe darf die Rechte-Matrix ändern.')}
  `;
}

function renderRangNamen(tab: OrgHierarchieTabView): string {
  const allowed = tab.myRights.includes('RANGNAMEN_AENDERN');
  return `
    <form class="org-form org-form--stacked" data-form="rank-names">
      ${tab.rangNamen.map((n, idx) => textInput(`rangname-${String(idx)}`, { placeholder: `Stufe ${String(idx + 1)}`, value: n, maxlength: 24, disabled: !allowed })).join('')}
      <button type="submit" class="btn-org btn-org--accent" ${disabledAttr(!allowed)}>RANGNAMEN SPEICHERN</button>
    </form>
    ${renderGrayedHint(!allowed, 'Dein Rang darf die Rangnamen nicht ändern.')}
  `;
}

function renderAufstieg(tab: OrgHierarchieTabView): string {
  const allowed = tab.myRights.includes('BEDINGUNGEN_SETZEN');
  return `
    <form class="org-form org-form--stacked" data-form="aufstieg">
      ${['2', '3', '4', '5'].map((stufe) => {
        const b = tab.aufstiegsBedingungen[stufe] ?? { typ: 'KEINE', wert: 0 };
        return `
          <div class="org-aufstieg-row">
            <span class="org-aufstieg-stufe">${esc(tab.rangNamen[Number(stufe) - 1] ?? `Stufe ${stufe}`)}:</span>
            <select name="typ-${stufe}" class="org-select" ${disabledAttr(!allowed)}>
              ${AUFSTIEG_TYPEN.map(([v, l]) => `<option value="${v}" ${v === b.typ ? 'selected' : ''}>${esc(l)}</option>`).join('')}
            </select>
            <input type="number" name="wert-${stufe}" value="${String(b.wert)}" min="0" max="100000" ${NO_AUTOFILL} ${disabledAttr(!allowed)} />
          </div>
        `;
      }).join('')}
      <label class="org-form-label">Automatischer Aufstieg bis Stufe</label>
      <select name="autoAufstiegMaxStufe" class="org-select" ${disabledAttr(!allowed)}>
        ${[1, 2, 3, 4, 5].map((s) => `<option value="${String(s)}" ${s === tab.autoAufstiegMaxStufe ? 'selected' : ''}>${String(s)}</option>`).join('')}
      </select>
      <button type="submit" class="btn-org btn-org--accent" ${disabledAttr(!allowed)}>BEDINGUNGEN SPEICHERN</button>
    </form>
    ${renderGrayedHint(!allowed, 'Dein Rang darf die Aufstiegsbedingungen nicht setzen.')}
  `;
}

function renderInvite(tab: OrgHierarchieTabView): string {
  const allowed = tab.myRights.includes('EINLADEN');
  return `
    <form class="org-form" data-form="invite">
      ${textInput('einladen-ziel', { placeholder: 'Exakter Spielername', maxlength: 20, disabled: !allowed })}
      <button type="submit" class="btn-org btn-org--accent" ${disabledAttr(!allowed)}>EINLADEN</button>
    </form>
    <p class="org-hint">Beitreten erfordert mindestens Level ${String(tab.beitrittMinLevel)}.</p>
    ${renderGrayedHint(!allowed, 'Dein Rang darf niemanden einladen.')}
    ${allowed && tab.pendingInvitesSent.length > 0 ? `
      <div class="org-sent-invite-list">
        ${tab.pendingInvitesSent.map((i) => `
          <div class="org-sent-invite-row">
            <span>${esc(i.invitedUsername)}</span>
            <span class="org-invite-meta">läuft ab ${fmtDate(i.expiresAt)}</span>
          </div>
        `).join('')}
      </div>` : ''}
  `;
}

function renderBeitrittSettings(tab: OrgHierarchieTabView): string {
  const allowed = tab.isTopRank;
  return `
    <form class="org-form org-form--stacked" data-form="settings">
      <label class="org-form-label">Beitrittsart</label>
      <select name="beitrittsart" class="org-select" ${disabledAttr(!allowed)}>
        ${(['OFFEN', 'BEWERBUNG', 'GESCHLOSSEN'] as const).map((a) => `<option value="${a}" ${a === tab.beitrittsart ? 'selected' : ''}>${esc(BEITRITTSART_LABEL[a])}</option>`).join('')}
      </select>
      <label class="org-form-label">Öffentliche Beschreibung</label>
      <textarea name="beschreibung" class="org-select" rows="3" maxlength="${String(ORG_BESCHREIBUNG_MAX_LENGTH)}" ${NO_AUTOFILL} ${disabledAttr(!allowed)}>${esc(tab.beschreibung ?? '')}</textarea>
      <button type="submit" class="btn-org btn-org--accent" ${disabledAttr(!allowed)}>SPEICHERN</button>
    </form>
    ${renderGrayedHint(!allowed, 'Nur die oberste Stufe legt fest, wie man beitritt.')}
  `;
}

function renderApplications(tab: OrgHierarchieTabView): string {
  if (!tab.isTopRank) return '';
  return `
    <p class="org-admin-subsection-label">BEWERBUNGEN</p>
    ${tab.applications.length === 0 ? '<p class="org-hint">Keine offenen Bewerbungen.</p>' : `
      <div class="org-payout-list">
        ${tab.applications.map((a) => `
          <div class="org-payout-row">
            <div class="org-payout-info">
              <span><strong>${esc(a.username)}</strong> · Level ${String(a.level)} · läuft ab ${fmtDate(a.expiresAt)}</span>
              <span class="org-invite-meta">„${esc(a.message)}"</span>
            </div>
            <div class="org-invite-actions">
              <button class="btn-org btn-org--small btn-org--accent" data-action="accept-application" data-application-id="${esc(a.id)}">ANNEHMEN</button>
              <button class="btn-org btn-org--small btn-org--danger" data-action="reject-application" data-application-id="${esc(a.id)}">ABLEHNEN</button>
            </div>
          </div>
        `).join('')}
      </div>
    `}
  `;
}

export function renderHierarchieTab(tab: OrgHierarchieTabView, myPlayerId: string): string {
  return `
    <div class="org-section">
      <p class="org-section-label">RANGLEITER</p>
      ${renderLadder(tab, myPlayerId)}
    </div>
    <div class="org-section">
      <p class="org-section-label">BEITRITT</p>
      <p class="org-admin-subsection-label">EINLADEN</p>
      ${renderInvite(tab)}
      ${renderApplications(tab)}
      <p class="org-admin-subsection-label">IM VERZEICHNIS</p>
      ${renderBeitrittSettings(tab)}
    </div>
    <div class="org-section">
      <p class="org-section-label">RECHTE</p>
      ${renderMatrix(tab)}
    </div>
    <div class="org-section">
      <p class="org-section-label">RANGNAMEN</p>
      ${renderRangNamen(tab)}
    </div>
    <div class="org-section">
      <p class="org-section-label">AUFSTIEGSBEDINGUNGEN</p>
      ${renderAufstieg(tab)}
    </div>
  `;
}

// ─── AUSBAU ───────────────────────────────────────────────────────────────────────

function renderZweig(zw: OrgTalentZweigView, baum: OrgBaumView, tab: OrgAusbauTabView, expanded: boolean, respecLocked: boolean): string {
  const canSkillen = tab.myRights.includes('SKILLEN');
  const investBlocked = !canSkillen || baum.punkteVerfuegbar <= 0 || zw.stufe >= 12;
  const investReason = !canSkillen
    ? 'Dein Rang darf keine Punkte setzen.'
    : zw.stufe >= 12 ? 'Dieser Zweig ist bereits auf der Höchststufe.' : 'Kein freier Skillpunkt verfügbar.';
  const respecBlocked = !tab.isTopRank || respecLocked || zw.stufe <= 0;
  const respecReason = !tab.isTopRank
    ? 'Nur die oberste Stufe darf zurücksetzen.'
    : respecLocked ? 'Respec-Sperre läuft noch.' : 'In diesem Zweig ist kein Punkt investiert.';

  return `
    <div class="org-baum-zweig">
      <button type="button" class="org-baum-zweig-title" data-action="toggle-zweig" data-zweig="${esc(zw.zweig)}">
        ${expanded ? '&#9660;' : '&#9654;'} ${esc(ZWEIG_LABELS[zw.zweig] ?? zw.zweig)} — Stufe ${String(zw.stufe)}/12
      </button>
      ${expanded ? `
        <div class="org-baum-knoten-list">
          ${zw.knoten.map((k) => `<span class="org-baum-knoten ${k.erreicht ? 'org-baum-knoten--erreicht' : ''}" title="${esc(k.name)}: ${esc(k.beschreibung)}">${String(k.stufe)}</span>`).join('')}
        </div>
        <div class="org-baum-zweig-actions">
          <button class="btn-org btn-org--small btn-org--accent" data-action="baum-invest" data-zweig="${esc(zw.zweig)}" ${disabledAttr(investBlocked)}>PUNKT SETZEN</button>
          ${renderGrayedHint(investBlocked, investReason)}
          <button class="btn-org btn-org--small btn-org--danger" data-action="baum-respec" data-zweig="${esc(zw.zweig)}" ${disabledAttr(respecBlocked)}>
            ZURÜCKSETZEN (${fmtAmount(baum.naechsteRespecKostenCents)} ₦, ${String(baum.respecWartezeitStunden)} Std. Sperre)
          </button>
          ${renderGrayedHint(respecBlocked, respecReason)}
        </div>
      ` : ''}
    </div>
  `;
}

export function renderAusbauTab(
  tab: OrgAusbauTabView,
  expandedZweige: ReadonlySet<string>,
  now: Date = new Date(),
): string {
  const baum = tab.baum;
  const z = baum.zentrale;
  const canUpgrade = tab.myRights.includes('SKILLEN');
  const ausbauLaeuft = z.ausbauCompletesAt !== null && !z.isAusbauMoeglich;
  const respecLocked = baum.respecSperreBis !== null && new Date(baum.respecSperreBis) > now;

  let zentraleAction: string;
  if (ausbauLaeuft) {
    zentraleAction = `<p class="org-hint">Ausbau läuft — fertig am ${fmtDate(z.ausbauCompletesAt ?? '')}.</p>`;
  } else if (z.naechsteStufe) {
    zentraleAction = `
      <button class="btn-org btn-org--accent" data-action="zentrale-upgrade" ${disabledAttr(!canUpgrade)}>
        AUSBAU STARTEN (${fmtAmount(z.naechsteStufe.kostenNoxCents)} ₦, ${String(z.naechsteStufe.dauerStunden)} Std.)
      </button>
      ${renderGrayedHint(!canUpgrade, 'Dein Rang darf die Zentrale nicht ausbauen.')}
    `;
  } else {
    zentraleAction = '<p class="org-hint">Höchststufe erreicht.</p>';
  }

  // X4 — Nenner ist das absolute Maximum (42), nicht die bisher erreichte Stufe.
  const xpLine = baum.xpFuerNaechstenPunkt === null
    ? 'Alle Punkte freigeschaltet.'
    : `Org-XP bis zum nächsten Punkt: ${String(baum.orgXp)} / ${String(baum.xpFuerNaechstenPunkt)}`;

  return `
    <div class="org-section">
      <p class="org-section-label">ZENTRALE</p>
      <p class="org-meta">Stufe ${String(z.stufe)} von ${String(z.maxStufe)} · Mitgliederlimit ${String(z.effectiveMemberLimit)}</p>
      ${zentraleAction}
    </div>
    <div class="org-section">
      <p class="org-section-label">TALENTBAUM</p>
      <p class="org-meta">
        Punkte freigeschaltet: <strong>${String(baum.orgStufe)} / ${String(baum.maxPunkte)}</strong> ·
        vergeben: ${String(baum.punkteVergeben)} · verfügbar: <strong>${String(baum.punkteVerfuegbar)}</strong>
      </p>
      <p class="org-hint">${esc(xpLine)} — Org-XP kommen aus Brennpunkt-Abgaben und Aufträgen, nicht aus Einzahlungen.</p>
      ${respecLocked ? `<p class="org-hint">Respec-Sperre bis ${fmtDate(baum.respecSperreBis ?? '')}.</p>` : ''}
      <div class="org-baum-zweige">
        ${baum.zweige.map((zw) => renderZweig(zw, baum, tab, expandedZweige.has(zw.zweig), respecLocked)).join('')}
      </div>
    </div>
  `;
}

// ─── GELÄNDE (NT-ORG-05, umgebaut NT-ORG-08) ──────────────────────────────────────

const RISIKO_LABEL: Record<string, string> = { KEINS: 'KEINS', NIEDRIG: 'NIEDRIG', MITTEL: 'MITTEL', HOCH: 'HOCH' };
const ZWECK_LABEL: Record<OrgGebaeudeZweck, string> = {
  LAGER: 'LAGER', ANBAU: 'ANBAU', LABOR: 'LABOR', FAHRZEUGE: 'FAHRZEUGE', SICHERHEIT: 'SICHERHEIT', VERWALTUNG: 'VERWALTUNG',
};

/** Aufgeklappte Bereiche im Tab GELÄNDE (nur Anzeige, lebt in org.ts). */
export interface OrgGelaendeUiState {
  /** ÖFFNEN — Details je Gebäude. */
  offeneGebaeude: ReadonlySet<string>;
  /** BEBAUEN — für welche freie Parzelle die Bauauswahl offen ist. */
  bauParzelleId: string | null;
  /** ANLAGE AUSBAUEN — für welches Gebäude die Ausbau-Wahl (Kauf/Kette) offen ist. */
  anlageGebaeudeId: string | null;
}

/** D4 — Risiko aller Org-Gebäude: höchstes Fahndungslevel unter den Mitgliedern, samt Wachhaus. */
export function renderGebaeudeRisiko(r: OrgGelaendeTabView['risiko']): string {
  const fl = r.username
    ? `HÖCHSTES FAHNDUNGSLEVEL: ${esc(r.username)} (FL ${String(r.hoechsterFl)})`
    : 'HÖCHSTES FAHNDUNGSLEVEL: niemand gesucht (FL 0)';
  const teile: string[] = [];
  if (r.schutzReduktionPercent > 0) teile.push(`Schutz-Zweig −${String(r.schutzReduktionPercent)} %`);
  teile.push(r.wachhausReduktionPercent > 0 ? `Wachhaus −${String(r.wachhausReduktionPercent)} %` : 'kein Wachhaus');
  return `<p class="org-meta org-gebaeude-risiko"><strong>${fl}</strong> · Razzia-Risiko des Geländes: ${teile.join(' · ')}</p>`;
}

function itemOptions(items: readonly OrgGebaeudeItemView[], formatItem: (key: string) => string): string {
  return items
    .filter((i) => i.quantity > 0 && i.itemType !== 'VEHICLE')
    .map((i) => `<option value="${esc(`${i.itemType}|${i.itemKey}`)}">${esc(formatItem(i.itemKey))} (${String(i.quantity)})</option>`)
    .join('');
}

function renderWareForm(
  g: OrgGebaeudeKarteView,
  richtung: 'ZUM_GEBAEUDE' | 'INS_LAGER',
  items: readonly OrgGebaeudeItemView[],
  vehicles: readonly { vehicleKey: string; label: string }[],
  darf: boolean,
  formatItem: (key: string) => string,
): string {
  const options = itemOptions(items, formatItem);
  const titel = richtung === 'ZUM_GEBAEUDE' ? 'AUS DEM ORG-LAGER HOLEN' : 'INS ORG-LAGER BRINGEN';
  if (options.length === 0) {
    return `<p class="org-hint">${titel}: ${richtung === 'ZUM_GEBAEUDE' ? 'Org-Lager ist leer.' : 'Im Gebäude liegt nichts.'}</p>`;
  }
  const blocked = !darf || vehicles.length === 0;
  return `
    <form class="org-form org-gebaeude-ware" data-form="gebaeude-ware" data-gebaeude-id="${esc(g.id)}" data-richtung="${richtung}">
      <label class="org-form-label">${titel}</label>
      <select name="ware" ${disabledAttr(blocked)}>${options}</select>
      <input type="number" name="menge" min="1" max="999" value="1" inputmode="numeric" ${NO_AUTOFILL} ${disabledAttr(blocked)} />
      <select name="fahrzeug" ${disabledAttr(blocked)}>
        ${vehicles.map((v) => `<option value="${esc(v.vehicleKey)}">${esc(v.label)}</option>`).join('')}
      </select>
      <button type="submit" class="btn-org btn-org--small" ${disabledAttr(blocked)}>TRANSPORT STARTEN</button>
    </form>
  `;
}

/** D3 — Bauauswahl: ausschließlich die Org-Typen, mit Zweck, Mindestfläche und Preis. */
function renderBauauswahl(p: OrgGelaendeParzelleView, tab: OrgGelaendeTabView, darfBauen: boolean): string {
  const zeilen = tab.katalog.map((k) => {
    let grund: string | null = null;
    if (k.bereitsGebaut) grund = 'schon vorhanden';
    else if (k.minFlaecheQm > p.flaecheQm) grund = `braucht ${String(k.minFlaecheQm)} m²`;
    const blocked = !darfBauen || grund !== null;
    return `
      <div class="org-bau-option">
        <div class="org-bau-option-text">
          <span><strong>${esc(k.label.toUpperCase())}</strong> · ${ZWECK_LABEL[k.zweck]} · ab ${String(k.minFlaecheQm)} m²</span>
          <span class="org-hint">${esc(k.beschreibung)}</span>
        </div>
        <button class="btn-org btn-org--small" data-action="gebaeude-bauen" data-parcel-id="${esc(p.id)}" data-typ="${esc(k.typ)}" ${disabledAttr(blocked)}>
          BAUEN (${fmtAmount(k.baupreisCents)} ₦)
        </button>
        ${grund ? `<span class="org-meta">${esc(grund.toUpperCase())}</span>` : ''}
      </div>
    `;
  }).join('');
  return `
    <div class="org-gelaende-panel">
      <p class="org-hint">Ein neues Gebäude startet auf Anlagenstufe 0 und produziert erst ab Stufe 1.</p>
      ${zeilen}
    </div>
  `;
}

/** C3/W1/W2 — Anlagenstufe kaufen (sofort, teuer) oder per Kette beschaffen. */
function renderAnlageAuswahl(g: OrgGebaeudeKarteView, tab: OrgGelaendeTabView, now: Date): string {
  const n = g.naechsteStufe;
  if (!n) return '';
  const darfBauen = tab.myRights.includes('BAUEN');
  const kaufLaeuft = tab.anlagenAusbauBis !== null && new Date(tab.anlagenAusbauBis) > now;
  let kaufGrund: string | null = null;
  if (!darfBauen) kaufGrund = 'Dein Rang darf nicht bauen.';
  else if (kaufLaeuft) kaufGrund = `Es läuft schon ein gekaufter Ausbau (bis ${fmtDate(tab.anlagenAusbauBis ?? '')}).`;
  else if (g.ketteLaeuft) kaufGrund = 'Für diese Anlage läuft gerade eine Kette.';
  const ketteGrund = tab.isTopRank ? tab.ketteBlockiert : 'Nur die oberste Stufe startet Ketten.';
  const fortschritt = g.fortschrittPercent > 0
    ? `<p class="org-hint">Bisheriger Kettenfortschritt für Stufe ${String(n.zielStufe)}: ${String(g.fortschrittPercent)} % — wird auf die nächste Kette angerechnet.</p>`
    : '';
  return `
    <div class="org-gelaende-panel">
      <p class="org-meta">STUFE ${String(n.zielStufe)}: ${esc(n.wirkung)}</p>
      ${fortschritt}
      <div class="org-header-actions">
        <button class="btn-org btn-org--small" data-action="anlage-kauf" data-gebaeude-id="${esc(g.id)}" ${disabledAttr(kaufGrund !== null)}>
          KAUFEN (${fmtAmount(n.kaufKostenCents)} ₦, ${String(Math.round(n.minuten / 60))} Std.)
        </button>
        <button class="btn-org btn-org--small" data-action="anlage-kette" data-gebaeude-id="${esc(g.id)}" ${disabledAttr(ketteGrund !== null)}>
          ALS KETTE (Einsatz ${fmtAmount(n.ketteEinsatzCents)} ₦)
        </button>
      </div>
      ${kaufGrund ? `<p class="org-grayed-hint">KAUF: ${esc(kaufGrund)}</p>` : ''}
      ${ketteGrund ? `<p class="org-grayed-hint">KETTE: ${esc(ketteGrund)}</p>` : ''}
      <p class="org-hint">Kaufen geht sofort los, kostet aber deutlich mehr. Die Kette läuft über den Tab AUFTRÄGE und hebt die Stufe ab 50 % Erfüllung.</p>
    </div>
  `;
}

function renderGebaeudeDetails(
  g: OrgGebaeudeKarteView,
  tab: OrgGelaendeTabView,
  vehicles: readonly { vehicleKey: string; label: string }[],
  formatItem: (key: string) => string,
): string {
  const darfProduzieren = tab.myRights.includes('PRODUKTION');
  const raid = g.raidStatus;
  const raidZeile = raid
    ? `<p class="org-meta">RAZZIA-RISIKO: <strong>${RISIKO_LABEL[raid.riskLabel] ?? esc(raid.riskLabel)}</strong> (${raid.dailyChancePercent.toFixed(2).replace('.', ',')} % / Tag) · Verdacht ${String(Math.round(g.suspicionLevel))} %
        ${raid.warningActive ? ' · <span class="org-gebaeude-warnung">NACHBARN REDEN</span>' : ''}
        ${raid.immuneUntil ? ` · sicher bis ${fmtDate(raid.immuneUntil)}` : ''}</p>`
    : '';
  let sprung = '';
  if (g.zweck === 'ANBAU') sprung = '<button class="btn-org btn-org--small" data-action="gebaeude-oeffnen-ziel" data-ziel="/gewaechshaus">ZUM GEWÄCHSHAUS</button>';
  else if (g.zweck === 'LABOR') sprung = '<button class="btn-org btn-org--small" data-action="gebaeude-oeffnen-ziel" data-ziel="/labor">ZUM LABOR</button>';
  const produktion = g.zweck === 'ANBAU' || g.zweck === 'LABOR'
    ? ` · Pflanzen ${String(g.aktivePflanzen)} · Kochvorgänge ${String(g.aktiveKochvorgaenge)}`
    : '';
  const ware = g.hatWarenpuffer ? `
    <p class="org-section-label">WARE</p>
    ${g.inventory.length > 0
      ? `<p class="org-meta">${g.inventory.map((i) => `${esc(formatItem(i.itemKey))} ${String(i.quantity)}`).join(' · ')}</p>`
      : '<p class="org-hint">Leer.</p>'}
    ${renderWareForm(g, 'ZUM_GEBAEUDE', tab.orgLagerInventory, vehicles, darfProduzieren, formatItem)}
    ${renderWareForm(g, 'INS_LAGER', g.inventory, vehicles, darfProduzieren, formatItem)}
    ${darfProduzieren && vehicles.length === 0 ? '<p class="org-grayed-hint">Kein freies Fahrzeug in deiner Garage.</p>' : ''}
    ${renderGrayedHint(!darfProduzieren, 'Dein Rang darf keine Ware bewegen.')}
  ` : '';
  return `
    <div class="org-gelaende-panel">
      <p class="org-meta">Belegt ${String(g.belegteSlots)} / ${String(g.slots)} Slots${produktion}</p>
      ${raidZeile}
      ${sprung ? `<div class="org-header-actions">${sprung}</div>` : ''}
      ${ware}
    </div>
  `;
}

function renderParzelle(
  p: OrgGelaendeParzelleView,
  tab: OrgGelaendeTabView,
  ui: OrgGelaendeUiState,
  vehicles: readonly { vehicleKey: string; label: string }[],
  formatItem: (key: string) => string,
  now: Date,
): string {
  const darfBauen = tab.myRights.includes('BAUEN');
  const g = p.gebaeude;
  const aktionen: string[] = [];
  let kopfStatus: string;
  let unten = '';

  if (!g) {
    kopfStatus = 'FREI';
    aktionen.push(`<button class="btn-org btn-org--small" data-action="gelaende-bauauswahl" data-parcel-id="${esc(p.id)}" ${disabledAttr(!darfBauen)}>BEBAUEN</button>`);
    if (ui.bauParzelleId === p.id) unten = renderBauauswahl(p, tab, darfBauen);
  } else {
    const ausbau = g.ausbauFertigAm !== null ? ` · Ausbau fertig am ${fmtDate(g.ausbauFertigAm)}` : '';
    kopfStatus = `${g.label.toUpperCase()} · ANLAGENSTUFE ${String(g.anlagenStufe)}/${String(g.maxStufe)}${ausbau}`;
    const offen = ui.offeneGebaeude.has(g.id);
    aktionen.push(`<button class="btn-org btn-org--small" data-action="gebaeude-toggle" data-gebaeude-id="${esc(g.id)}">${offen ? 'SCHLIESSEN' : 'ÖFFNEN'}</button>`);
    if (g.naechsteStufe && g.ausbauFertigAm === null) {
      aktionen.push(`<button class="btn-org btn-org--small" data-action="anlage-auswahl" data-gebaeude-id="${esc(g.id)}">ANLAGE AUSBAUEN</button>`);
    }
    const abreissbar = g.typ !== 'UMSCHLAGLAGER';
    if (abreissbar) {
      const blocked = !darfBauen || g.abrissSperrgrund !== null;
      aktionen.push(`<button class="btn-org btn-org--danger btn-org--small" data-action="gebaeude-abreissen" data-gebaeude-id="${esc(g.id)}" ${disabledAttr(blocked)}>ABREISSEN (+${fmtAmount(g.abrissErstattungCents)} ₦)</button>`);
    }
    unten = `
      <p class="org-meta">${esc(g.wirkung)}</p>
      ${abreissbar && g.abrissSperrgrund ? `<p class="org-grayed-hint">ABREISSEN: ${esc(g.abrissSperrgrund)}</p>` : ''}
      ${ui.anlageGebaeudeId === g.id ? renderAnlageAuswahl(g, tab, now) : ''}
      ${offen ? renderGebaeudeDetails(g, tab, vehicles, formatItem) : ''}
    `;
  }

  if (p.ausbauCompletesAt !== null) {
    aktionen.push(`<span class="org-meta">Parzelle wird erweitert — fertig am ${fmtDate(p.ausbauCompletesAt)}</span>`);
  } else if (p.nextFlaecheQm !== null && p.nextCostCents !== null) {
    aktionen.push(`
      <button class="btn-org btn-org--small" data-action="gelaende-ausbau" data-parcel-id="${esc(p.id)}" ${disabledAttr(!darfBauen)}>
        PARZELLE ERWEITERN AUF ${String(p.nextFlaecheQm)} m² (${fmtAmount(p.nextCostCents)} ₦, ${String(Math.round((p.nextMinutes ?? 0) / 60))} Std.)
      </button>`);
  }

  return `
    <div class="org-parzelle-karte">
      <div class="org-gebaeude-kopf">
        <span><strong>NR. ${String(p.houseNumber)}</strong> · ${String(p.flaecheQm)} m²</span>
        <span class="org-meta">${esc(kopfStatus)}</span>
      </div>
      <div class="org-header-actions">${aktionen.join('')}</div>
      ${unten}
    </div>
  `;
}

/** D1 — Tab GELÄNDE: Gelände-Kopf, Risiko-Zeile, Parzellen als Zeilen (mobil: Karten). */
export function renderGelaendeTab(
  tab: OrgGelaendeTabView,
  ui: OrgGelaendeUiState,
  vehicles: readonly { vehicleKey: string; label: string }[] = [],
  formatItem: (key: string) => string = (k) => k,
  now: Date = new Date(),
): string {
  const darfBauen = tab.myRights.includes('BAUEN');
  let entfernung = 'Du hast noch kein eigenes Viertel.';
  if (tab.entfernungSchritte !== null) {
    entfernung = `Entfernung zu deinem Viertel: ${String(tab.entfernungSchritte)} Straßen (${tab.gleicherBezirk ? 'gleicher Bezirk' : 'anderer Bezirk'}).`;
  }
  return `
    <div class="org-section">
      <p class="org-section-label">GELÄNDE</p>
      <p class="org-meta"><strong>${esc(tab.name)}</strong> · Bezirk ${esc(tab.districtName)}</p>
      <p class="org-meta">${esc(entfernung)}</p>
      ${renderGebaeudeRisiko(tab.risiko)}
      <p class="org-hint">Das Gelände ist keine Straße: keine Ecken, keine Dealer, kein Einfluss. Neue Parzellen kommen mit dem Ausbau der Zentrale. Ware kommt nur aus dem Org-Lager und geht nur dorthin zurück.</p>
      <div class="org-gelaende-parzellen">
        ${tab.parzellen.map((p) => renderParzelle(p, tab, ui, vehicles, formatItem, now)).join('')}
      </div>
      ${renderGrayedHint(!darfBauen, 'Dein Rang darf auf dem Gelände nicht bauen.')}
      <form class="org-form" data-form="gelaende-name">
        <label class="org-form-label">Name des Geländes</label>
        ${textInput('gelaende-titel', { placeholder: 'Neuer Name', maxlength: 30, value: tab.name, disabled: !darfBauen })}
        <button type="submit" class="btn-org btn-org--small" ${disabledAttr(!darfBauen)}>UMBENENNEN</button>
      </form>
    </div>
    ${renderSpedition(tab.spedition, now)}
    <div class="org-section">
      <p class="org-section-label">GELÄNDE-LOG</p>
      ${renderLogList(tab.log, 'Noch nichts passiert.')}
    </div>
  `;
}

const ORG_FAHRZEUG_ZUSTAND_LABEL: Record<OrgFahrzeugZustand, string> = {
  FREI: 'FREI',
  UNTERWEGS: 'UNTERWEGS',
  BESCHLAGNAHMT: 'BESCHLAGNAHMT',
};

/** NT-ORG-09 — Abschnitt SPEDITION: Plätze, Org-Fahrzeuge mit Zustand, Kaufen, Verkaufen, Kaution. */
export function renderSpedition(s: OrgSpeditionView, now: Date = new Date()): string {
  if (!s.hatSpedition) {
    return `
      <div class="org-section">
        <p class="org-section-label">SPEDITION</p>
        <p class="org-hint">Ohne Spedition hat die Organisation keine eigenen Fahrzeuge. Transportaufgaben laufen dann mit den privaten Fahrzeugen der Mitglieder.</p>
      </div>
    `;
  }
  const kasse = Number(s.kasseNoxCents);
  const voll = s.belegt >= s.plaetze;
  const keinRecht = 'Dein Rang darf keine Org-Fahrzeuge verwalten.';
  const fahrzeuge = s.fahrzeuge.length === 0
    ? '<p class="org-meta">Noch keine Fahrzeuge in der Spedition.</p>'
    : s.fahrzeuge.map((f) => {
      let aktion = '';
      if (f.zustand === 'BESCHLAGNAHMT') {
        const frei = f.seizedUntil ? ` · frei in ${fmtRestzeit(f.seizedUntil, now)}` : '';
        aktion = `
          <span class="org-meta">Kaution ${fmtAmount(f.kautionCents)} ₦${frei}</span>
          <button type="button" class="btn-org btn-org--small btn-org--accent" data-action="org-fahrzeug-kaution" data-fahrzeug-id="${esc(f.id)}" ${disabledAttr(!s.darfVerwalten || kasse < Number(f.kautionCents))}>KAUTION ZAHLEN</button>`;
      } else if (f.zustand === 'FREI') {
        aktion = `<button type="button" class="btn-org btn-org--small btn-org--danger" data-action="org-fahrzeug-verkaufen" data-fahrzeug-id="${esc(f.id)}" ${disabledAttr(!s.darfVerwalten)}>VERKAUFEN (+${fmtAmount(f.verkaufserloesCents)} ₦)</button>`;
      }
      return `
        <div class="org-fahrzeug-zeile">
          <span><strong>${esc(f.label)}</strong> · ${String(f.capacitySlots)} Slots</span>
          <span class="org-fahrzeug-zustand org-fahrzeug-zustand--${f.zustand.toLowerCase()}">${ORG_FAHRZEUG_ZUSTAND_LABEL[f.zustand]}</span>
          <span class="org-fahrzeug-aktion">${aktion}</span>
        </div>`;
    }).join('');
  const kaufGrund = !s.darfVerwalten ? keinRecht : voll ? 'Alle Plätze belegt — Anlage ausbauen oder ein Fahrzeug verkaufen.' : '';
  return `
    <div class="org-section">
      <p class="org-section-label">SPEDITION</p>
      <p class="org-meta">Plätze: <strong>${String(s.belegt)} / ${String(s.plaetze)}</strong> (Anlagenstufe ${String(s.anlagenStufe)}) · Kasse ${fmtAmount(s.kasseNoxCents)} ₦</p>
      <p class="org-hint">Org-Fahrzeuge fahren nur Org-Transportaufgaben. Wer eine Aufgabe übernimmt, leiht ein passendes freies Fahrzeug; ist keins frei, braucht er ein eigenes.</p>
      <div class="org-fahrzeug-liste">${fahrzeuge}</div>
      ${renderGrayedHint(!s.darfVerwalten && s.fahrzeuge.length > 0, keinRecht)}
      <form class="org-form" data-form="org-fahrzeug-kaufen">
        <label class="org-form-label">Fahrzeug kaufen (aus der Kasse)</label>
        <select name="fahrzeug" class="org-select" required ${disabledAttr(kaufGrund !== '')}>
          <option value="" disabled selected>— FAHRZEUG WÄHLEN —</option>
          ${s.katalog.map((k) => `<option value="${esc(k.vehicleKey)}" ${disabledAttr(kasse < Number(k.preisCents))}>${esc(k.label)} · ${String(k.capacitySlots)} Slots · ${fmtAmount(k.preisCents)} ₦</option>`).join('')}
        </select>
        <button type="submit" class="btn-org btn-org--small btn-org--accent" ${disabledAttr(kaufGrund !== '')}>KAUFEN</button>
      </form>
      ${renderGrayedHint(kaufGrund !== '', kaufGrund)}
    </div>
  `;
}

// ─── AUFTRÄGE (NT-ORG-07a) ────────────────────────────────────────────────────────

function waehrungZeichen(currency: string): string {
  return currency === 'BLACK_MONEY' ? 'S-₦' : '₦';
}

/** Restzeit als "2 T 5 Std" / "3 Std 20 Min" / "12 Min". */
export function fmtRestzeit(iso: string, now: Date): string {
  const ms = Math.max(0, new Date(iso).getTime() - now.getTime());
  const minuten = Math.floor(ms / 60_000);
  const tage = Math.floor(minuten / 1440);
  const stunden = Math.floor((minuten % 1440) / 60);
  const rest = minuten % 60;
  if (tage > 0) return `${String(tage)} T ${String(stunden)} Std`;
  if (stunden > 0) return `${String(stunden)} Std ${String(rest)} Min`;
  return `${String(rest)} Min`;
}

function aufgabeTypLabel(a: OrgAufgabeView): string {
  return a.aufgabenTyp === 'TRANSPORT'
    ? `TRANSPORT · ${String(a.gebundeneSlots)} Slots`
    : `BUNKERN · ${String(a.gebundeneSlots)} Slots · ${String(a.stunden)} Std`;
}

export interface OrgAufgabeFormState { aufgabeId: string }

function renderUebernehmenForm(a: OrgAufgabeView, tab: OrgAuftraegeTabView): string {
  const labelVon = (id: string): string => tab.objekte.find((o) => o.id === id)?.label ?? 'Objekt';
  if (a.aufgabenTyp === 'TRANSPORT') {
    const paare = tab.paare.filter((p) => p.maxSlots >= a.gebundeneSlots);
    const fahrzeuge = tab.fahrzeuge.filter((f) => f.capacitySlots >= a.gebundeneSlots);
    // NT-ORG-09 N1 — steht ein Org-Fahrzeug bereit, fährt die Aufgabe damit; keine Auswahl nötig.
    const fahrzeugFeld = a.orgFahrzeug
      ? `<p class="org-meta">Fahrzeug: <strong>${esc(a.orgFahrzeug.label)}</strong> aus der Spedition</p>`
      : `
        <label class="org-form-label">Eigenes Fahrzeug</label>
        <select name="fahrzeug" class="org-select" required>
          <option value="" disabled selected>— FAHRZEUG WÄHLEN —</option>
          ${fahrzeuge.map((f) => `<option value="${esc(f.vehicleKey)}">${esc(f.label)} (${String(f.capacitySlots)} Slots)</option>`).join('')}
        </select>`;
    return `
      <form class="org-auftrag-form" data-form="org-aufgabe-uebernehmen" data-aufgabe-id="${esc(a.id)}" data-typ="TRANSPORT">
        <label class="org-form-label">Strecke</label>
        <select name="paar" class="org-select" required>
          <option value="" disabled selected>— STRECKE WÄHLEN —</option>
          ${paare.map((p) => `<option value="${esc(p.quelleId)}|${esc(p.zielId)}">${esc(labelVon(p.quelleId))} → ${esc(labelVon(p.zielId))} (${String(p.steps)} Straßen)</option>`).join('')}
        </select>
        ${fahrzeugFeld}
        <div class="org-auftrag-form-actions">
          <button type="submit" class="btn-org btn-org--small btn-org--accent">LOSFAHREN</button>
          <button type="button" class="btn-org btn-org--small" data-action="org-aufgabe-abbrechen">ABBRECHEN</button>
        </div>
      </form>
    `;
  }
  const objekte = tab.objekte.filter((o) => o.freeSlots >= a.gebundeneSlots);
  return `
    <form class="org-auftrag-form" data-form="org-aufgabe-uebernehmen" data-aufgabe-id="${esc(a.id)}" data-typ="BUNKERN">
      <label class="org-form-label">Wo lagerst du die Kiste ein?</label>
      <select name="objekt" class="org-select" required>
        <option value="" disabled selected>— OBJEKT WÄHLEN —</option>
        ${objekte.map((o) => `<option value="${esc(o.id)}">${esc(o.label)} (${String(o.freeSlots)} frei)</option>`).join('')}
      </select>
      <div class="org-auftrag-form-actions">
        <button type="submit" class="btn-org btn-org--small btn-org--accent">EINLAGERN</button>
        <button type="button" class="btn-org btn-org--small" data-action="org-aufgabe-abbrechen">ABBRECHEN</button>
      </div>
    </form>
  `;
}

function renderAufgabeZeile(a: OrgAufgabeView, auftrag: OrgAuftragView, tab: OrgAuftraegeTabView, offenesFormular: string | null): string {
  const lohn = `${fmtAmount(a.lohnCents)} ${waehrungZeichen(auftrag.currency)}`;
  let aktion: string;
  if (a.zustand === 'ERLEDIGT') {
    aktion = `<span class="org-auftrag-status org-auftrag-status--erledigt">ERLEDIGT · ${esc(a.erledigtVonName ?? '')} · −${String(a.zeitabzugMinuten ?? 0)} Min</span>`;
  } else if (a.istMeine) {
    aktion = `
      <span class="org-auftrag-status org-auftrag-status--meine">DEINE AUFGABE — läuft auf dem Auftragsbrett</span>
      <button type="button" class="btn-org btn-org--small btn-org--danger" data-action="org-aufgabe-zurueckgeben" data-aufgabe-id="${esc(a.id)}">ZURÜCKGEBEN</button>
    `;
  } else if (a.zustand === 'UEBERNOMMEN') {
    aktion = '<span class="org-auftrag-status">IN ARBEIT</span>';
  } else {
    aktion = `
      <button type="button" class="btn-org btn-org--small btn-org--accent" data-action="org-aufgabe-oeffnen" data-aufgabe-id="${esc(a.id)}" ${disabledAttr(a.sperrgrund !== null)}>ÜBERNEHMEN</button>
      ${renderGrayedHint(a.sperrgrund !== null, a.sperrgrund ?? '')}
    `;
  }
  // NT-ORG-09 N3 — vor dem Übernehmen sichtbar, welches Fahrzeug die Aufgabe braucht.
  let fahrzeugHinweis = '';
  if (a.aufgabenTyp === 'TRANSPORT' && a.zustand === 'OFFEN') {
    fahrzeugHinweis = a.orgFahrzeug
      ? `<p class="org-meta org-fahrzeug-hinweis org-fahrzeug-hinweis--org">ORG-FAHRZEUG: ${esc(a.orgFahrzeug.label.toUpperCase())}</p>`
      : '<p class="org-meta org-fahrzeug-hinweis">EIGENES FAHRZEUG NÖTIG</p>';
  }
  return `
    <div class="org-auftrag-aufgabe">
      <div class="org-auftrag-aufgabe-kopf">
        <span>${aufgabeTypLabel(a)}</span>
        <span class="org-meta">Lohn ${lohn}</span>
      </div>
      ${fahrzeugHinweis}
      <div class="org-auftrag-aufgabe-aktion">${aktion}</div>
      ${offenesFormular === a.id ? renderUebernehmenForm(a, tab) : ''}
    </div>
  `;
}

function renderStufe(s: OrgStufeView, auftrag: OrgAuftragView, tab: OrgAuftraegeTabView, expanded: boolean, offenesFormular: string | null): string {
  const aufgaben = auftrag.aufgaben.filter((a) => a.stufe === s.stufe);
  // T4 — aktueller Abzug je Aufgabe und was Vorarbeit eine Stufe tiefer noch bringt.
  const verstaerkung = s.stufe > 1
    ? s.vorarbeitBisMax > 0
      ? ` · bis ${String(s.maxZeitabzugMinuten)} Min mit ${String(s.vorarbeitBisMax)} weiteren Aufgaben auf Stufe ${String(s.stufe - 1)}`
      : ' · volle Verstärkung'
    : '';
  return `
    <div class="org-auftrag-stufe ${s.istMeineStufe ? 'org-auftrag-stufe--meine' : ''}">
      <button type="button" class="org-auftrag-stufe-kopf" data-action="org-toggle-stufe" data-stufe="${String(s.stufe)}">
        ${expanded ? '&#9660;' : '&#9654;'} STUFE ${String(s.stufe)} · ${esc(s.rangLabel)} · ${String(s.erledigt)}/${String(s.aufgabenGesamt)}
        ${s.istMeineStufe ? '<span class="org-auftrag-badge">DEINE STUFE</span>' : ''}
      </button>
      <p class="org-meta">Je Aufgabe −${String(s.aktuellerZeitabzugMinuten)} Min${verstaerkung}</p>
      ${expanded ? `<div class="org-auftrag-aufgaben">${aufgaben.map((a) => renderAufgabeZeile(a, auftrag, tab, offenesFormular)).join('')}</div>` : ''}
    </div>
  `;
}

function renderLaufenderAuftrag(auftrag: OrgAuftragView, tab: OrgAuftraegeTabView, expandedStufen: ReadonlySet<number>, offenesFormular: string | null, now: Date): string {
  const zeichen = waehrungZeichen(auftrag.currency);
  const erledigt = auftrag.aufgaben.filter((a) => a.zustand === 'ERLEDIGT').length;
  const ausgezahlt = Number(auftrag.einsatzCents) - Number(auftrag.einsatzRestCents);
  const untergrenzeErreicht = new Date(auftrag.endsAt).getTime() <= new Date(auftrag.minEndsAt).getTime();
  return `
    <div class="org-section">
      <p class="org-section-label">LAUFENDER AUFTRAG — ${esc(auftrag.name.toUpperCase())}</p>
      ${auftrag.anlageZiel ? `<p class="org-meta">Ziel: ${esc(auftrag.anlageZiel.label)} auf Anlagenstufe ${String(auftrag.anlageZiel.zielStufe)} (ab 50 % Erfüllung).</p>` : ''}
      <p class="org-auftrag-timer">Endet in <strong>${fmtRestzeit(auftrag.endsAt, now)}</strong></p>
      <p class="org-meta">
        ${untergrenzeErreicht ? 'Schneller geht es nicht mehr — der Auftrag läuft bis zur Mindestdauer.' : `Frühestens fertig in ${fmtRestzeit(auftrag.minEndsAt, now)}.`}
      </p>
      <p class="org-meta">
        Erledigt ${String(erledigt)}/${String(auftrag.aufgaben.length)} (${String(Math.round(auftrag.quote * 100))} %) ·
        Löhne ausgezahlt ${fmtAmount(String(ausgezahlt))} von ${fmtAmount(auftrag.einsatzCents)} ${zeichen}
      </p>
      <p class="org-hint">Jede erledigte Aufgabe zieht Zeit vom Timer ab. Arbeit auf einer tieferen Stufe macht künftige Aufgaben eine Stufe höher wirksamer — nie rückwirkend. Unter 50 % Erfüllung gibt es keine Org-XP.</p>
      ${tab.istOberste
        ? '<button type="button" class="btn-org btn-org--small btn-org--danger" data-action="org-auftrag-abbrechen">AUFTRAG ABBRECHEN</button>'
        : ''}
    </div>
    <div class="org-section">
      <p class="org-section-label">KETTE</p>
      ${auftrag.stufen.map((s) => renderStufe(s, auftrag, tab, expandedStufen.has(s.stufe), offenesFormular)).join('')}
    </div>
  `;
}

function renderVorlagen(tab: OrgAuftraegeTabView, now: Date): string {
  const cooldown = tab.cooldownBis !== null && new Date(tab.cooldownBis) > now;
  const karten = tab.vorlagen.map((v) => {
    const kasse = Number(v.currency === 'BLACK_MONEY' ? tab.kasseBlackMoneyCents : tab.kasseNoxCents);
    const zuWenig = kasse < Number(v.einsatzCents);
    const gesperrt = !tab.istOberste || cooldown || zuWenig;
    const grund = !tab.istOberste
      ? 'Nur die oberste Stufe startet Aufträge.'
      : cooldown ? `Pause bis ${fmtDate(tab.cooldownBis ?? '')}.` : 'Nicht genug in der Kasse.';
    return `
      <div class="org-auftrag-vorlage">
        <p class="org-auftrag-vorlage-name">${esc(v.name.toUpperCase())}</p>
        <p class="org-hint">${esc(v.beschreibung)}</p>
        <p class="org-meta">
          Einsatz ${fmtAmount(v.einsatzCents)} ${waehrungZeichen(v.currency)} · ${String(v.laufzeitTage)} Tage ·
          ${String(v.kettenStufen)} Stufen · ${String(v.aufgabenGesamt)} Aufgaben
        </p>
        <p class="org-meta">Bei voller Erfüllung: +${String(v.orgXp)} Org-XP · ${String(v.endproduktMenge)}× ${esc(v.endproduktLabel)} ins Org-Lager</p>
        <button type="button" class="btn-org btn-org--small btn-org--accent" data-action="org-auftrag-starten" data-vorlage="${esc(v.key)}" ${disabledAttr(gesperrt)}>STARTEN</button>
        ${renderGrayedHint(gesperrt, grund)}
      </div>
    `;
  }).join('');
  return `
    <div class="org-section">
      <p class="org-section-label">AUFTRAG STARTEN</p>
      <p class="org-hint">Der Einsatz kommt aus der Org-Kasse und wird als Lohn an die Mitglieder ausgezahlt. Was übrig bleibt, geht zurück in die Kasse.</p>
      ${karten}
    </div>
  `;
}

function renderLetzterAuftrag(a: OrgAuftragView): string {
  const status = a.status === 'ABGEBROCHEN' ? 'ABGEBROCHEN' : `${String(Math.round(a.quote * 100))} % ERFÜLLT`;
  return `
    <div class="org-section">
      <p class="org-section-label">LETZTER AUFTRAG</p>
      <p class="org-meta">${esc(a.name)} · ${status}${a.orgXpVergeben !== null ? ` · +${String(a.orgXpVergeben)} Org-XP` : ''}${a.completedAt ? ` · ${fmtDate(a.completedAt)}` : ''}</p>
    </div>
  `;
}

export function renderAuftraegeTab(
  tab: OrgAuftraegeTabView,
  expandedStufen: ReadonlySet<number>,
  offenesFormular: string | null,
  now: Date = new Date(),
): string {
  const kopf = tab.laufend
    ? renderLaufenderAuftrag(tab.laufend, tab, expandedStufen, offenesFormular, now)
    : renderVorlagen(tab, now);
  return kopf + (tab.letzter ? renderLetzterAuftrag(tab.letzter) : '');
}

// ─── PRODUKTION (NT-ORG-07b) — umgekehrte Pyramide ───────────────────────────────

/** Umschalter KETTE / PRODUKTION im Tab AUFTRÄGE. */
export function renderAuftraegeAnsichtSwitch(active: OrgAuftraegeAnsicht): string {
  const btn = (key: OrgAuftraegeAnsicht, label: string): string =>
    `<button type="button" class="org-subtab-btn ${key === active ? 'is-active' : ''}" data-action="org-auftraege-ansicht" data-ansicht="${key}">${label}</button>`;
  return `<nav class="org-subtabs org-subtabs--inline" aria-label="Auftragsart">${btn('KETTE', 'KETTE')}${btn('PRODUKTION', 'PRODUKTION')}</nav>`;
}

function renderFahndungsdruck(druck: OrgFahndungsdruck): string {
  return `<p class="org-meta">FAHNDUNGSDRUCK: <span class="org-prod-druck org-prod-druck--${druck}">${druck}</span></p>`;
}

function renderBeteiligte(b: OrgBeteiligterView[]): string {
  if (b.length === 0) return '<p class="org-meta">Noch niemand.</p>';
  return `<p class="org-meta">Beteiligt: ${b.map((x) => `${esc(x.name)} (${String(x.laeufe)}×)`).join(', ')}</p>`;
}

function renderEbeneKopf(ebene: OrgProdEbene, titel: string, collapsed: ReadonlySet<OrgProdEbene>): string {
  return `
    <button type="button" class="org-auftrag-stufe-kopf" data-action="org-prod-toggle" data-ebene="${ebene}">
      ${collapsed.has(ebene) ? '&#9654;' : '&#9660;'} ${titel}
    </button>
  `;
}

function renderMeinLauf(lauf: NonNullable<OrgProduktionTabView['meinLauf']>, now: Date): string {
  const was = lauf.typ === 'BESORGUNG' ? `${String(lauf.menge)}× ${esc(lauf.itemLabel)} besorgen` : `${esc(lauf.itemLabel)} herstellen`;
  const zeit = lauf.wartetAufPlatz
    ? '<span class="org-auftrag-status org-auftrag-status--meine">WARTET AUF PLATZ IM ORG-LAGER</span>'
    : `<span class="org-auftrag-status org-auftrag-status--meine">fertig in ${fmtRestzeit(lauf.endsAt, now)}</span>`;
  return `
    <div class="org-section org-prod-meinlauf">
      <p class="org-section-label">DEIN ORG-JOB</p>
      <p class="org-meta">${was}</p>
      ${zeit}
      <p class="org-hint">Läuft in einem eigenen Slot — deine normale Schicht bleibt frei.</p>
    </div>
  `;
}

function renderLaufendeProduktion(p: OrgProduktionView, tab: OrgProduktionTabView, collapsed: ReadonlySet<OrgProdEbene>, now: Date): string {
  const zeichen = waehrungZeichen(p.currency);
  const ausgegeben = Number(p.einsatzCents) - Number(p.einsatzRestCents);
  const fensterOffen = new Date(p.fensterEndeAt).getTime() > now.getTime();
  const v = p.verarbeitung;

  const endproduktBody = collapsed.has('ENDPRODUKT')
    ? ''
    : `<p class="org-prod-menge">${String(p.produktMenge)}× hergestellt</p>
       <p class="org-hint">Geht ins Org-Lager. Mehr Mitmachende füllen die Werkbänke schneller — mehr, als die Werkbänke schaffen, geht nie.</p>`;
  const endprodukt = `
    <div class="org-auftrag-stufe org-prod-ebene org-prod-ebene--endprodukt">
      ${renderEbeneKopf('ENDPRODUKT', `ENDPRODUKT · ${esc(p.endproduktLabel.toUpperCase())}`, collapsed)}
      ${endproduktBody}
    </div>
  `;

  const verarbeitungBody = collapsed.has('VERARBEITUNG')
    ? ''
    : `<p class="org-meta">Je Durchlauf ${String(v.mengeJeLauf)}× ${esc(v.outputLabel)} · ${String(v.dauerMinuten)} Min · Lohn ${fmtAmount(v.lohnJeLaufCents)} ${zeichen}</p>
       <p class="org-meta">Werkbänke: ${String(v.werkbaenke)} · braucht das Recht PRODUKTION</p>
       ${renderBeteiligte(v.beteiligte)}
       <div class="org-auftrag-aufgabe-aktion">
         <button type="button" class="btn-org btn-org--small btn-org--accent" data-action="org-job-verarbeiten" ${disabledAttr(v.sperrgrund !== null)}>VERARBEITEN</button>
         ${renderGrayedHint(v.sperrgrund !== null, v.sperrgrund ?? '')}
       </div>`;
  const laufendText = v.laufend > 0 ? ` · ${String(v.laufend)} läuft` : '';
  const verarbeitung = `
    <div class="org-auftrag-stufe org-prod-ebene">
      ${renderEbeneKopf('VERARBEITUNG', `VERARBEITUNG · ${String(v.erledigt)} Durchläufe${laufendText}`, collapsed)}
      ${verarbeitungBody}
    </div>
  `;

  const besorgungRows = p.besorgung.map((b) => {
    const illegal = b.illegal ? ' <span class="org-prod-druck org-prod-druck--MITTEL">ILLEGAL</span>' : '';
    const laeuft = b.laufend > 0 ? ` · ${String(b.laufend)} läuft` : '';
    return `
      <div class="org-auftrag-aufgabe">
        <div class="org-auftrag-aufgabe-kopf">
          <span>${esc(b.label)}${illegal}</span>
          <span class="org-meta">im Lager ${String(b.imLager)} · je Verarbeitung ${String(b.bedarfJeVerarbeitung)}</span>
        </div>
        <p class="org-meta">Je Lauf ${String(b.mengeJeLauf)}× · ${String(b.dauerMinuten)} Min · Lohn ${fmtAmount(b.lohnJeLaufCents)} ${zeichen} · ${String(b.erledigt)} erledigt${laeuft}</p>
        ${renderBeteiligte(b.beteiligte)}
        <div class="org-auftrag-aufgabe-aktion">
          <button type="button" class="btn-org btn-org--small btn-org--accent" data-action="org-job-besorgen" data-item="${esc(b.itemKey)}" ${disabledAttr(b.sperrgrund !== null)}>BESORGEN</button>
          ${renderGrayedHint(b.sperrgrund !== null, b.sperrgrund ?? '')}
        </div>
      </div>
    `;
  }).join('');
  const fensterText = fensterOffen ? ` · noch ${fmtRestzeit(p.fensterEndeAt, now)}` : ' · Fenster vorbei';
  const besorgung = `
    <div class="org-auftrag-stufe org-prod-ebene">
      ${renderEbeneKopf('BESORGUNG', `BESORGUNG · für alle Ränge${fensterText}`, collapsed)}
      ${collapsed.has('BESORGUNG') ? '' : `<div class="org-auftrag-aufgaben">${besorgungRows}</div>`}
    </div>
  `;

  const verlauf = p.verlauf.length === 0 ? '' : `
    <div class="org-section">
      <p class="org-section-label">LETZTE ORG-JOBS</p>
      <div class="org-log-list">${p.verlauf.map((j) => `
        <div class="org-log-entry">
          <span class="org-log-time">${j.completedAt ? fmtDate(j.completedAt) : ''}</span>
          <span class="org-log-message">${esc(j.playerName)}: ${String(j.ausbeute ?? 0)}× ${esc(j.itemLabel)} · ${fmtAmount(j.lohnCents)} ${zeichen}${j.ereignisText ? ` — ${esc(j.ereignisText)}` : ''}</span>
        </div>`).join('')}
      </div>
    </div>
  `;

  const fensterHinweis = fensterOffen
    ? `Besorgen geht noch ${fmtRestzeit(p.fensterEndeAt, now)} (bis ${String(tab.beschaffungsFensterProzent)} % der Laufzeit), danach nur noch Verarbeitung.`
    : 'Das Beschaffungsfenster ist vorbei — jetzt wird nur noch verarbeitet.';
  return `
    <div class="org-section">
      <p class="org-section-label">LAUFENDE PRODUKTION — ${esc(p.name.toUpperCase())}</p>
      <p class="org-auftrag-timer">Endet in <strong>${fmtRestzeit(p.endsAt, now)}</strong></p>
      <p class="org-meta">${fensterHinweis}</p>
      <p class="org-meta">Aus dem Einsatz verbraucht ${fmtAmount(String(ausgegeben))} von ${fmtAmount(p.einsatzCents)} ${zeichen} (Löhne und Material). Der Rest geht am Ende zurück in die Kasse.</p>
      ${tab.istOberste ? '<button type="button" class="btn-org btn-org--small btn-org--danger" data-action="org-prod-abbrechen">PRODUKTION ABBRECHEN</button>' : ''}
    </div>
    <div class="org-section org-prod-pyramide">
      ${endprodukt}
      ${verarbeitung}
      ${besorgung}
    </div>
    ${verlauf}
  `;
}

function renderProduktionsAngebote(tab: OrgProduktionTabView, now: Date): string {
  const cooldown = tab.cooldownBis !== null && new Date(tab.cooldownBis) > now;
  const karten = tab.angebote.map((a) => {
    const zeichen = waehrungZeichen(a.currency);
    const kasse = Number(a.currency === 'BLACK_MONEY' ? tab.kasseBlackMoneyCents : tab.kasseNoxCents);
    const guenstigste = Number(a.laufzeiten[0]?.einsatzCents ?? '0');
    const grund = a.sperrgrund ?? (kasse < guenstigste ? 'NICHT GENUG IN DER KASSE' : null);
    const zutaten = a.zutaten.map((z) => `${String(z.menge)}× ${esc(z.label)}${z.illegal ? ' (illegal)' : ''}`).join(', ');
    const optionen = a.laufzeiten
      .map((l) => `<option value="${String(l.tage)}">${String(l.tage)} ${l.tage === 1 ? 'Tag' : 'Tage'} · Einsatz ${fmtAmount(l.einsatzCents)} ${zeichen}</option>`)
      .join('');
    return `
      <div class="org-auftrag-vorlage">
        <p class="org-auftrag-vorlage-name">${esc(a.name.toUpperCase())}</p>
        <p class="org-hint">${esc(a.beschreibung)}</p>
        ${renderFahndungsdruck(a.fahndungsdruck)}
        <p class="org-meta">Endprodukt: ${esc(a.endproduktLabel)} · Zutaten je Verarbeitung: ${zutaten}</p>
        <p class="org-meta">Besorgung: ${String(a.besorgungMenge)}× je Lauf, ${String(a.besorgungMinuten)} Min, Lohn ${fmtAmount(a.lohnBesorgungCents)} ${zeichen}</p>
        <p class="org-meta">Verarbeitung: ${String(a.outputJeLauf)}× je Lauf, ${String(a.verarbeitungMinuten)} Min, Lohn ${fmtAmount(a.lohnVerarbeitungCents)} ${zeichen}</p>
        <p class="org-meta">Höchstens ${String(a.maxMengeJeTag)}× ${esc(a.endproduktLabel)} am Tag (${String(a.werkbaenke)} passende Werkbänke)</p>
        <div class="org-form">
          <select class="org-select" id="org-prod-laufzeit-${esc(a.key)}" ${disabledAttr(grund !== null)}>${optionen}</select>
          <button type="button" class="btn-org btn-org--small btn-org--accent" data-action="org-prod-starten" data-angebot="${esc(a.key)}" ${disabledAttr(grund !== null)}>STARTEN</button>
        </div>
        ${renderGrayedHint(grund !== null, grund ?? '')}
      </div>
    `;
  }).join('');
  return `
    <div class="org-section">
      <p class="org-section-label">PRODUKTION STARTEN</p>
      <p class="org-hint">Die oberste Stufe kauft eine Laufzeit. Mitglieder besorgen Zutaten (alle Ränge) und verarbeiten sie im Org-Labor (Recht PRODUKTION). Wie viel herauskommt, hängt allein davon ab, wie viele wie oft mitmachen.</p>
      ${cooldown ? `<p class="org-hint">Pause bis ${fmtDate(tab.cooldownBis ?? '')}.</p>` : ''}
      ${karten}
    </div>
  `;
}

export function renderProduktionTab(tab: OrgProduktionTabView, collapsed: ReadonlySet<OrgProdEbene>, now: Date = new Date()): string {
  const mein = tab.meinLauf ? renderMeinLauf(tab.meinLauf, now) : '';
  const haupt = tab.laufend ? renderLaufendeProduktion(tab.laufend, tab, collapsed, now) : renderProduktionsAngebote(tab, now);
  let letzte = '';
  if (tab.letzte) {
    const l = tab.letzte;
    const status = l.status === 'ABGEBROCHEN' ? 'ABGEBROCHEN' : 'BEENDET';
    letzte = `
      <div class="org-section">
        <p class="org-section-label">LETZTE PRODUKTION</p>
        <p class="org-meta">${esc(l.name)} · ${status} · ${String(l.produktMenge)}× ${esc(l.endproduktLabel)}${l.completedAt ? ` · ${fmtDate(l.completedAt)}` : ''}</p>
      </div>
    `;
  }
  return mein + haupt + letzte;
}

// ─── Org-Verkauf (NT-ORG-07b, S1–S5) ──────────────────────────────────────────────

const KANAL_LABEL: Record<OrgVerkaufKanal, string> = { HEHLER: 'HEHLER', SCHWARZMARKT: 'SCHWARZMARKT', STRASSENHANDEL: 'STRASSENHANDEL' };

function renderVerkauf(v: OrgVerkaufView): string {
  if (!v.darfVerkaufen) {
    return `
      <div class="org-section">
        <p class="org-section-label">VERKAUFEN</p>
        ${renderGrayedHint(true, 'Verkaufen braucht das Recht VERKAUFEN (Standard: Stufe 4 und 5).')}
      </div>
    `;
  }
  const gesperrt = v.restHeute <= 0;
  const rows = v.items.length === 0
    ? '<p class="org-hint">Nichts im Org-Lager, das Hehler, Schwarzmarkt oder Straßenhandel nehmen.</p>'
    : v.items.map((i) => `
      <form class="org-form org-verkauf-row" data-form="org-verkauf" data-item="${esc(i.itemKey)}">
        <span class="org-lager-item-name">${esc(i.label)} (${String(i.quantity)})</span>
        <select name="kanal" class="org-select">${i.kanaele.map((k) => `<option value="${k}">${KANAL_LABEL[k]}</option>`).join('')}</select>
        <input type="number" name="menge" class="org-qty-input" min="1" max="${String(Math.max(1, Math.min(i.quantity, v.restHeute)))}" value="1" ${NO_AUTOFILL} ${disabledAttr(gesperrt)} />
        <button type="submit" class="btn-org btn-org--small btn-org--accent" ${disabledAttr(gesperrt)}>VERKAUFEN</button>
      </form>
    `).join('');
  return `
    <div class="org-section">
      <p class="org-section-label">VERKAUFEN</p>
      <p class="org-hint">Der Erlös geht in die Org-Kasse. Der Hehler zahlt nach Ablauf der Kommission, beim Schwarzmarkt führst du die Suche in deinem Schwarzmarkt-Tab zu Ende. Legale Ware (Bier, Schnaps, Parfüm) nimmt der Straßenhandel sofort, mit 20–30 % Abschlag. Der Dealer-Kanal bleibt Spielern vorbehalten.</p>
      <p class="org-meta">Heute noch ${String(v.restHeute)} von ${String(v.limit)} Einheiten.</p>
      ${rows}
    </div>
  `;
}

// ─── LAGER ────────────────────────────────────────────────────────────────────────

export function renderLagerTab(tab: OrgLagerTabView, formatItem: (key: string) => string): string {
  const lager = tab.lager;
  const itemsHtml = lager.inventory.length === 0
    ? '<p class="org-hint">Lager ist leer.</p>'
    : `<div class="org-lager-item-list">${lager.inventory.map((i) => `
        <div class="org-lager-item-row">
          <span class="org-lager-item-name">${esc(formatItem(i.itemKey))}</span>
          <span class="org-lager-item-qty">${String(i.quantity)}</span>
        </div>
      `).join('')}</div>`;

  return `
    <div class="org-section">
      <p class="org-section-label">ORG-LAGER</p>
      <p class="org-meta">${String(lager.currentLoad)} / ${String(lager.slots)} Slots belegt</p>
      ${itemsHtml}
      <div class="org-lager-actions">
        <button class="btn-org btn-org--accent" data-action="open-lager-deposit">EINLAGERN</button>
        <button class="btn-org btn-org--accent" data-action="open-lager-withdraw" ${disabledAttr(!lager.canWithdraw)}>ENTNEHMEN</button>
        ${lager.canWithdraw ? '' : '<button class="btn-org" data-action="open-lager-request">ANFRAGE STELLEN</button>'}
      </div>
      ${lager.canWithdraw
        ? `<p class="org-hint">Restlimit heute: ${String(lager.entnahmeRemainingSlots ?? 0)} Slots.</p>`
        : renderGrayedHint(true, `Entnehmen dürfen nur die Stufen ${String(lager.entnahmeMinRank)} und 5 — stell eine Anfrage.`)}
    </div>
    ${renderVerkauf(tab.verkauf)}
    <div class="org-section">
      <p class="org-section-label">BEWEGUNGEN</p>
      ${renderLogList(tab.log, 'Noch keine Lagerbewegungen.')}
    </div>
  `;
}

// ─── VERZEICHNIS + LEADERBOARD (Teile E/F) ───────────────────────────────────────

export interface OrgDirectoryFilter { search: string; sort: 'members' | 'stufe' | 'alter'; freiePlaetze: boolean }
export type OrgLeaderboardSort = 'stufe' | 'members' | 'punkte';

function renderJoinControl(entry: OrgDirectoryEntry, applyOpen: boolean): string {
  if (entry.joinState === 'BEITRETEN') {
    return `<button class="btn-org btn-org--accent" data-action="join-org" data-org-id="${esc(entry.id)}">BEITRETEN</button>`;
  }
  if (entry.joinState === 'BEWERBEN') {
    if (!applyOpen) return `<button class="btn-org btn-org--accent" data-action="open-apply" data-org-id="${esc(entry.id)}">BEWERBEN</button>`;
    return `
      <form class="org-form org-form--stacked" data-form="apply" data-org-id="${esc(entry.id)}">
        <textarea name="bewerbung" class="org-select" rows="2" maxlength="${String(ORG_BEWERBUNG_TEXT_MAX_LENGTH)}" placeholder="Kurz: warum du?" ${NO_AUTOFILL} required></textarea>
        <div class="org-form">
          <button type="submit" class="btn-org btn-org--accent">BEWERBUNG SENDEN</button>
          <button type="button" class="btn-org" data-action="close-apply">ABBRECHEN</button>
        </div>
      </form>
    `;
  }
  const label = entry.joinState === 'BEWORBEN' ? 'BEWORBEN' : entry.beitrittsart === 'BEWERBUNG' ? 'BEWERBEN' : 'BEITRETEN';
  return `
    <button class="btn-org" disabled>${label}</button>
    ${renderGrayedHint(true, entry.blockedReason ?? 'Nicht möglich.')}
  `;
}

const BEITRITTSART_KURZ: Record<OrgBeitrittsart, string> = { OFFEN: 'OFFEN', BEWERBUNG: 'BEWERBUNG', GESCHLOSSEN: 'GESCHLOSSEN' };

export function renderDirectoryList(result: OrgDirectoryResult, filter: OrgDirectoryFilter, applyOpenOrgId: string | null): string {
  const entriesHtml = result.entries.length === 0
    ? '<p class="org-hint">Keine Organisation gefunden.</p>'
    : result.entries.map((e) => `
      <div class="org-dir-card">
        <div class="org-dir-head">
          <span class="org-dir-name">${esc(e.name)}</span>
          <span class="org-dir-badge">${BEITRITTSART_KURZ[e.beitrittsart]}</span>
        </div>
        <p class="org-invite-meta">
          ${String(e.memberCount)} / ${String(e.memberLimit)} Mitglieder · Zentrale Stufe ${String(e.zentralenStufe)} ·
          Gelände: ${String(e.gebaeudeAnzahl)} Gebäude · höchste Anlagenstufe ${String(e.hoechsteAnlagenStufe)} ·
          Abgabensatz ${fmtPercent(e.tributeSharePercent / 100)} % · gegründet ${new Date(e.createdAt).toLocaleDateString('de-DE')}
        </p>
        ${e.beschreibung ? `<p class="org-dir-desc">${esc(e.beschreibung)}</p>` : ''}
        <div class="org-dir-actions">${renderJoinControl(e, applyOpenOrgId === e.id)}</div>
      </div>
    `).join('');

  return `
    <div class="org-section">
      <p class="org-section-label">ORGANISATIONEN</p>
      <form class="org-form" data-form="directory-search">
        ${textInput('suche', { placeholder: 'Nach Namen suchen', value: filter.search, maxlength: 30, required: false })}
        <select name="sortierung" class="org-select" aria-label="Sortierung">
          <option value="members" ${filter.sort === 'members' ? 'selected' : ''}>Mitglieder</option>
          <option value="stufe" ${filter.sort === 'stufe' ? 'selected' : ''}>Zentralenstufe</option>
          <option value="alter" ${filter.sort === 'alter' ? 'selected' : ''}>Älteste zuerst</option>
        </select>
        <label class="org-check"><input type="checkbox" name="freiePlaetze" ${filter.freiePlaetze ? 'checked' : ''} /> nur mit freien Plätzen</label>
        <button type="submit" class="btn-org">SUCHEN</button>
      </form>
      <div class="org-dir-list">${entriesHtml}</div>
      ${renderPager(result.page, result.pages, 'directory')}
    </div>
  `;
}

function renderPager(page: number, pages: number, target: 'directory' | 'leaderboard'): string {
  if (pages <= 1) return '';
  return `
    <div class="org-pager">
      <button class="btn-org btn-org--small" data-action="page-${target}" data-page="${String(page - 1)}" ${disabledAttr(page <= 1)}>ZURÜCK</button>
      <span class="org-invite-meta">Seite ${String(page)} / ${String(pages)}</span>
      <button class="btn-org btn-org--small" data-action="page-${target}" data-page="${String(page + 1)}" ${disabledAttr(page >= pages)}>WEITER</button>
    </div>
  `;
}

export function renderLeaderboard(result: OrgLeaderboardResult, sort: OrgLeaderboardSort): string {
  const sortBtn = (key: OrgLeaderboardSort, label: string): string =>
    `<button type="button" class="org-subtab-btn ${key === sort ? 'is-active' : ''}" data-action="leaderboard-sort" data-sort="${key}">${label}</button>`;
  return `
    <div class="org-section">
      <p class="org-section-label">ORG-RANGLISTE</p>
      <div class="org-subtabs org-subtabs--inline">
        ${sortBtn('stufe', 'ORG-STUFE')}${sortBtn('members', 'MITGLIEDER')}${sortBtn('punkte', 'TALENTPUNKTE')}
      </div>
      ${result.entries.length === 0 ? '<p class="org-hint">Noch keine Organisationen.</p>' : `
        <div class="org-matrix-wrap">
          <table class="org-matrix-table org-leaderboard-table">
            <thead><tr><th>#</th><th>NAME</th><th>ORG-STUFE</th><th>MITGLIEDER</th><th>PUNKTE</th></tr></thead>
            <tbody>
              ${result.entries.map((e) => `
                <tr><td>${String(e.position)}</td><td>${esc(e.name)}</td><td>${String(e.orgStufe)}</td><td>${String(e.memberCount)}</td><td>${String(e.punkteVergeben)}</td></tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `}
      ${renderPager(result.page, result.pages, 'leaderboard')}
    </div>
  `;
}

/** Eigene Einladungen, eigene Bewerbungen und das Gründungsformular — nur ohne Org relevant. */
export function renderNoOrgPanel(data: OrgPageData): string {
  const g = data.gruendung;
  const invitesHtml = data.invitesReceived.length === 0
    ? '<p class="org-hint">Keine offenen Einladungen.</p>'
    : data.invitesReceived.map((i) => `
      <div class="org-invite-row">
        <div class="org-invite-info">
          <span class="org-invite-org">${esc(i.orgName)}</span>
          <span class="org-invite-meta">eingeladen von ${esc(i.invitedByUsername)} · läuft ab ${fmtDate(i.expiresAt)} · Abgabensatz ${fmtPercent(i.tributeShare)} %</span>
        </div>
        <div class="org-invite-actions">
          <button class="btn-org btn-org--accent" data-action="accept-invite" data-invite-id="${esc(i.id)}">ANNEHMEN</button>
          <button class="btn-org" data-action="decline-invite" data-invite-id="${esc(i.id)}">ABLEHNEN</button>
        </div>
      </div>
    `).join('');

  const applicationsHtml = data.myApplications.length === 0 ? '' : `
    <p class="org-admin-subsection-label">MEINE BEWERBUNGEN</p>
    ${data.myApplications.map((a) => `
      <div class="org-invite-row">
        <div class="org-invite-info">
          <span class="org-invite-org">${esc(a.orgName)}</span>
          <span class="org-invite-meta">läuft ab ${fmtDate(a.expiresAt)}</span>
        </div>
        <button class="btn-org btn-org--small" data-action="withdraw-application" data-application-id="${esc(a.id)}">ZURÜCKZIEHEN</button>
      </div>
    `).join('')}
  `;

  return `
    <div class="org-locked-box">
      <p class="org-locked-text">DU BIST IN KEINER ORGANISATION</p>
      <div class="org-req-list">
        <p class="org-req-label">EINLADUNGEN</p>
        <p class="org-hint">Beitreten erfordert mindestens Level ${String(g.beitrittMinLevel)}.</p>
        ${invitesHtml}
        ${applicationsHtml}
      </div>
      <div class="org-req-list">
        <p class="org-req-label">EIGENE ORGANISATION GRÜNDEN</p>
        <p class="org-cost-hint">
          Kostet ${fmtAmount(g.kostenNoxCents)} ₦ und ${fmtAmount(g.kostenBlackMoneyCents)} S-₦ —
          beides sofort fällig, ohne Rückerstattung — und erfordert mindestens Level ${String(g.minLevel)}.
          Davon gehen ${fmtAmount(g.startkapitalNoxCents)} ₦ und ${fmtAmount(g.startkapitalBlackMoneyCents)} S-₦
          als Startkapital direkt in die neue Org-Kasse — der Rest ist die Gründungsgebühr.
          <button type="button" class="org-help-btn" data-action="help-org" title="Was bedeutet das genau?">?</button>
        </p>
        <form class="org-form" data-form="create-org">
          ${textInput('org-titel', { placeholder: 'Name der Organisation', maxlength: 30 })}
          <select name="rangVorlage" class="org-select" required>
            <option value="" disabled selected>— VORLAGE WÄHLEN —</option>
            ${RANG_VORLAGEN.map((v) => `<option value="${v}">${v}</option>`).join('')}
          </select>
          <button type="submit" class="btn-org btn-org--accent">GRÜNDEN</button>
        </form>
      </div>
    </div>
  `;
}
